import { p as proxyCustomElement, H, c as createEvent, h } from './p-ae815584.js';
import { k as forceFocus, n as nextTick, i as isNodeEnvironment, g as global$1, h as hasNamedSlot, b as bem } from './p-c44c2366.js';
import { t as tippy } from './p-5b08239b.js';
import { F as FormField } from './p-8e5cca04.js';
import { a as getAriaDescribedBy, c as getAriaDisabled, d as getAriaExpanded, g as getAriaInvalid, b as getAriaRequired } from './p-55335119.js';
import { d as defineCustomElement$b } from './p-7ca5e297.js';
import { d as defineCustomElement$a } from './p-3b23161c.js';
import { d as defineCustomElement$9 } from './p-6d444634.js';
import { d as defineCustomElement$8 } from './p-823765ed.js';
import { d as defineCustomElement$7 } from './p-757c507b.js';
import { d as defineCustomElement$6 } from './p-71f91db6.js';
import { d as defineCustomElement$5 } from './p-8a5ce1a3.js';
import { d as defineCustomElement$4 } from './p-cfb91ab4.js';
import { d as defineCustomElement$3 } from './p-8bbf2ff5.js';
import { d as defineCustomElement$2 } from './p-db87170c.js';
import { t as transformTagName, r as resolveAppearance } from './p-c01ca34a.js';

/**
 * Fuse.js v7.0.0 - Lightweight fuzzy-search (http://fusejs.io)
 *
 * Copyright (c) 2023 Kiro Risk (http://kiro.me)
 * All Rights Reserved. Apache Software License 2.0
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 */

function isArray(value) {
  return !Array.isArray
    ? getTag(value) === '[object Array]'
    : Array.isArray(value)
}

// Adapted from: https://github.com/lodash/lodash/blob/master/.internal/baseToString.js
const INFINITY = 1 / 0;
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value
  }
  let result = value + '';
  return result == '0' && 1 / value == -INFINITY ? '-0' : result
}

function toString(value) {
  return value == null ? '' : baseToString(value)
}

function isString(value) {
  return typeof value === 'string'
}

function isNumber(value) {
  return typeof value === 'number'
}

// Adapted from: https://github.com/lodash/lodash/blob/master/isBoolean.js
function isBoolean(value) {
  return (
    value === true ||
    value === false ||
    (isObjectLike(value) && getTag(value) == '[object Boolean]')
  )
}

function isObject(value) {
  return typeof value === 'object'
}

// Checks if `value` is object-like.
function isObjectLike(value) {
  return isObject(value) && value !== null
}

function isDefined(value) {
  return value !== undefined && value !== null
}

function isBlank(value) {
  return !value.trim().length
}

// Gets the `toStringTag` of `value`.
// Adapted from: https://github.com/lodash/lodash/blob/master/.internal/getTag.js
function getTag(value) {
  return value == null
    ? value === undefined
      ? '[object Undefined]'
      : '[object Null]'
    : Object.prototype.toString.call(value)
}

const EXTENDED_SEARCH_UNAVAILABLE = 'Extended search is not available';

const INCORRECT_INDEX_TYPE = "Incorrect 'index' type";

const LOGICAL_SEARCH_INVALID_QUERY_FOR_KEY = (key) =>
  `Invalid value for key ${key}`;

const PATTERN_LENGTH_TOO_LARGE = (max) =>
  `Pattern length exceeds max of ${max}.`;

const MISSING_KEY_PROPERTY = (name) => `Missing ${name} property in key`;

const INVALID_KEY_WEIGHT_VALUE = (key) =>
  `Property 'weight' in key '${key}' must be a positive integer`;

const hasOwn = Object.prototype.hasOwnProperty;

class KeyStore {
  constructor(keys) {
    this._keys = [];
    this._keyMap = {};

    let totalWeight = 0;

    keys.forEach((key) => {
      let obj = createKey(key);

      this._keys.push(obj);
      this._keyMap[obj.id] = obj;

      totalWeight += obj.weight;
    });

    // Normalize weights so that their sum is equal to 1
    this._keys.forEach((key) => {
      key.weight /= totalWeight;
    });
  }
  get(keyId) {
    return this._keyMap[keyId]
  }
  keys() {
    return this._keys
  }
  toJSON() {
    return JSON.stringify(this._keys)
  }
}

function createKey(key) {
  let path = null;
  let id = null;
  let src = null;
  let weight = 1;
  let getFn = null;

  if (isString(key) || isArray(key)) {
    src = key;
    path = createKeyPath(key);
    id = createKeyId(key);
  } else {
    if (!hasOwn.call(key, 'name')) {
      throw new Error(MISSING_KEY_PROPERTY('name'))
    }

    const name = key.name;
    src = name;

    if (hasOwn.call(key, 'weight')) {
      weight = key.weight;

      if (weight <= 0) {
        throw new Error(INVALID_KEY_WEIGHT_VALUE(name))
      }
    }

    path = createKeyPath(name);
    id = createKeyId(name);
    getFn = key.getFn;
  }

  return { path, id, weight, src, getFn }
}

function createKeyPath(key) {
  return isArray(key) ? key : key.split('.')
}

function createKeyId(key) {
  return isArray(key) ? key.join('.') : key
}

function get(obj, path) {
  let list = [];
  let arr = false;

  const deepGet = (obj, path, index) => {
    if (!isDefined(obj)) {
      return
    }
    if (!path[index]) {
      // If there's no path left, we've arrived at the object we care about.
      list.push(obj);
    } else {
      let key = path[index];

      const value = obj[key];

      if (!isDefined(value)) {
        return
      }

      // If we're at the last value in the path, and if it's a string/number/bool,
      // add it to the list
      if (
        index === path.length - 1 &&
        (isString(value) || isNumber(value) || isBoolean(value))
      ) {
        list.push(toString(value));
      } else if (isArray(value)) {
        arr = true;
        // Search each item in the array.
        for (let i = 0, len = value.length; i < len; i += 1) {
          deepGet(value[i], path, index + 1);
        }
      } else if (path.length) {
        // An object. Recurse further.
        deepGet(value, path, index + 1);
      }
    }
  };

  // Backwards compatibility (since path used to be a string)
  deepGet(obj, isString(path) ? path.split('.') : path, 0);

  return arr ? list : list[0]
}

const MatchOptions = {
  // Whether the matches should be included in the result set. When `true`, each record in the result
  // set will include the indices of the matched characters.
  // These can consequently be used for highlighting purposes.
  includeMatches: false,
  // When `true`, the matching function will continue to the end of a search pattern even if
  // a perfect match has already been located in the string.
  findAllMatches: false,
  // Minimum number of characters that must be matched before a result is considered a match
  minMatchCharLength: 1
};

const BasicOptions = {
  // When `true`, the algorithm continues searching to the end of the input even if a perfect
  // match is found before the end of the same input.
  isCaseSensitive: false,
  // When true, the matching function will continue to the end of a search pattern even if
  includeScore: false,
  // List of properties that will be searched. This also supports nested properties.
  keys: [],
  // Whether to sort the result list, by score
  shouldSort: true,
  // Default sort function: sort by ascending score, ascending index
  sortFn: (a, b) =>
    a.score === b.score ? (a.idx < b.idx ? -1 : 1) : a.score < b.score ? -1 : 1
};

const FuzzyOptions = {
  // Approximately where in the text is the pattern expected to be found?
  location: 0,
  // At what point does the match algorithm give up. A threshold of '0.0' requires a perfect match
  // (of both letters and location), a threshold of '1.0' would match anything.
  threshold: 0.6,
  // Determines how close the match must be to the fuzzy location (specified above).
  // An exact letter match which is 'distance' characters away from the fuzzy location
  // would score as a complete mismatch. A distance of '0' requires the match be at
  // the exact location specified, a threshold of '1000' would require a perfect match
  // to be within 800 characters of the fuzzy location to be found using a 0.8 threshold.
  distance: 100
};

const AdvancedOptions = {
  // When `true`, it enables the use of unix-like search commands
  useExtendedSearch: false,
  // The get function to use when fetching an object's properties.
  // The default will search nested paths *ie foo.bar.baz*
  getFn: get,
  // When `true`, search will ignore `location` and `distance`, so it won't matter
  // where in the string the pattern appears.
  // More info: https://fusejs.io/concepts/scoring-theory.html#fuzziness-score
  ignoreLocation: false,
  // When `true`, the calculation for the relevance score (used for sorting) will
  // ignore the field-length norm.
  // More info: https://fusejs.io/concepts/scoring-theory.html#field-length-norm
  ignoreFieldNorm: false,
  // The weight to determine how much field length norm effects scoring.
  fieldNormWeight: 1
};

var Config = {
  ...BasicOptions,
  ...MatchOptions,
  ...FuzzyOptions,
  ...AdvancedOptions
};

const SPACE = /[^ ]+/g;

// Field-length norm: the shorter the field, the higher the weight.
// Set to 3 decimals to reduce index size.
function norm(weight = 1, mantissa = 3) {
  const cache = new Map();
  const m = Math.pow(10, mantissa);

  return {
    get(value) {
      const numTokens = value.match(SPACE).length;

      if (cache.has(numTokens)) {
        return cache.get(numTokens)
      }

      // Default function is 1/sqrt(x), weight makes that variable
      const norm = 1 / Math.pow(numTokens, 0.5 * weight);

      // In place of `toFixed(mantissa)`, for faster computation
      const n = parseFloat(Math.round(norm * m) / m);

      cache.set(numTokens, n);

      return n
    },
    clear() {
      cache.clear();
    }
  }
}

class FuseIndex {
  constructor({
    getFn = Config.getFn,
    fieldNormWeight = Config.fieldNormWeight
  } = {}) {
    this.norm = norm(fieldNormWeight, 3);
    this.getFn = getFn;
    this.isCreated = false;

    this.setIndexRecords();
  }
  setSources(docs = []) {
    this.docs = docs;
  }
  setIndexRecords(records = []) {
    this.records = records;
  }
  setKeys(keys = []) {
    this.keys = keys;
    this._keysMap = {};
    keys.forEach((key, idx) => {
      this._keysMap[key.id] = idx;
    });
  }
  create() {
    if (this.isCreated || !this.docs.length) {
      return
    }

    this.isCreated = true;

    // List is Array<String>
    if (isString(this.docs[0])) {
      this.docs.forEach((doc, docIndex) => {
        this._addString(doc, docIndex);
      });
    } else {
      // List is Array<Object>
      this.docs.forEach((doc, docIndex) => {
        this._addObject(doc, docIndex);
      });
    }

    this.norm.clear();
  }
  // Adds a doc to the end of the index
  add(doc) {
    const idx = this.size();

    if (isString(doc)) {
      this._addString(doc, idx);
    } else {
      this._addObject(doc, idx);
    }
  }
  // Removes the doc at the specified index of the index
  removeAt(idx) {
    this.records.splice(idx, 1);

    // Change ref index of every subsquent doc
    for (let i = idx, len = this.size(); i < len; i += 1) {
      this.records[i].i -= 1;
    }
  }
  getValueForItemAtKeyId(item, keyId) {
    return item[this._keysMap[keyId]]
  }
  size() {
    return this.records.length
  }
  _addString(doc, docIndex) {
    if (!isDefined(doc) || isBlank(doc)) {
      return
    }

    let record = {
      v: doc,
      i: docIndex,
      n: this.norm.get(doc)
    };

    this.records.push(record);
  }
  _addObject(doc, docIndex) {
    let record = { i: docIndex, $: {} };

    // Iterate over every key (i.e, path), and fetch the value at that key
    this.keys.forEach((key, keyIndex) => {
      let value = key.getFn ? key.getFn(doc) : this.getFn(doc, key.path);

      if (!isDefined(value)) {
        return
      }

      if (isArray(value)) {
        let subRecords = [];
        const stack = [{ nestedArrIndex: -1, value }];

        while (stack.length) {
          const { nestedArrIndex, value } = stack.pop();

          if (!isDefined(value)) {
            continue
          }

          if (isString(value) && !isBlank(value)) {
            let subRecord = {
              v: value,
              i: nestedArrIndex,
              n: this.norm.get(value)
            };

            subRecords.push(subRecord);
          } else if (isArray(value)) {
            value.forEach((item, k) => {
              stack.push({
                nestedArrIndex: k,
                value: item
              });
            });
          } else ;
        }
        record.$[keyIndex] = subRecords;
      } else if (isString(value) && !isBlank(value)) {
        let subRecord = {
          v: value,
          n: this.norm.get(value)
        };

        record.$[keyIndex] = subRecord;
      }
    });

    this.records.push(record);
  }
  toJSON() {
    return {
      keys: this.keys,
      records: this.records
    }
  }
}

function createIndex(
  keys,
  docs,
  { getFn = Config.getFn, fieldNormWeight = Config.fieldNormWeight } = {}
) {
  const myIndex = new FuseIndex({ getFn, fieldNormWeight });
  myIndex.setKeys(keys.map(createKey));
  myIndex.setSources(docs);
  myIndex.create();
  return myIndex
}

function parseIndex(
  data,
  { getFn = Config.getFn, fieldNormWeight = Config.fieldNormWeight } = {}
) {
  const { keys, records } = data;
  const myIndex = new FuseIndex({ getFn, fieldNormWeight });
  myIndex.setKeys(keys);
  myIndex.setIndexRecords(records);
  return myIndex
}

function computeScore$1(
  pattern,
  {
    errors = 0,
    currentLocation = 0,
    expectedLocation = 0,
    distance = Config.distance,
    ignoreLocation = Config.ignoreLocation
  } = {}
) {
  const accuracy = errors / pattern.length;

  if (ignoreLocation) {
    return accuracy
  }

  const proximity = Math.abs(expectedLocation - currentLocation);

  if (!distance) {
    // Dodge divide by zero error.
    return proximity ? 1.0 : accuracy
  }

  return accuracy + proximity / distance
}

function convertMaskToIndices(
  matchmask = [],
  minMatchCharLength = Config.minMatchCharLength
) {
  let indices = [];
  let start = -1;
  let end = -1;
  let i = 0;

  for (let len = matchmask.length; i < len; i += 1) {
    let match = matchmask[i];
    if (match && start === -1) {
      start = i;
    } else if (!match && start !== -1) {
      end = i - 1;
      if (end - start + 1 >= minMatchCharLength) {
        indices.push([start, end]);
      }
      start = -1;
    }
  }

  // (i-1 - start) + 1 => i - start
  if (matchmask[i - 1] && i - start >= minMatchCharLength) {
    indices.push([start, i - 1]);
  }

  return indices
}

// Machine word size
const MAX_BITS = 32;

function search(
  text,
  pattern,
  patternAlphabet,
  {
    location = Config.location,
    distance = Config.distance,
    threshold = Config.threshold,
    findAllMatches = Config.findAllMatches,
    minMatchCharLength = Config.minMatchCharLength,
    includeMatches = Config.includeMatches,
    ignoreLocation = Config.ignoreLocation
  } = {}
) {
  if (pattern.length > MAX_BITS) {
    throw new Error(PATTERN_LENGTH_TOO_LARGE(MAX_BITS))
  }

  const patternLen = pattern.length;
  // Set starting location at beginning text and initialize the alphabet.
  const textLen = text.length;
  // Handle the case when location > text.length
  const expectedLocation = Math.max(0, Math.min(location, textLen));
  // Highest score beyond which we give up.
  let currentThreshold = threshold;
  // Is there a nearby exact match? (speedup)
  let bestLocation = expectedLocation;

  // Performance: only computer matches when the minMatchCharLength > 1
  // OR if `includeMatches` is true.
  const computeMatches = minMatchCharLength > 1 || includeMatches;
  // A mask of the matches, used for building the indices
  const matchMask = computeMatches ? Array(textLen) : [];

  let index;

  // Get all exact matches, here for speed up
  while ((index = text.indexOf(pattern, bestLocation)) > -1) {
    let score = computeScore$1(pattern, {
      currentLocation: index,
      expectedLocation,
      distance,
      ignoreLocation
    });

    currentThreshold = Math.min(score, currentThreshold);
    bestLocation = index + patternLen;

    if (computeMatches) {
      let i = 0;
      while (i < patternLen) {
        matchMask[index + i] = 1;
        i += 1;
      }
    }
  }

  // Reset the best location
  bestLocation = -1;

  let lastBitArr = [];
  let finalScore = 1;
  let binMax = patternLen + textLen;

  const mask = 1 << (patternLen - 1);

  for (let i = 0; i < patternLen; i += 1) {
    // Scan for the best match; each iteration allows for one more error.
    // Run a binary search to determine how far from the match location we can stray
    // at this error level.
    let binMin = 0;
    let binMid = binMax;

    while (binMin < binMid) {
      const score = computeScore$1(pattern, {
        errors: i,
        currentLocation: expectedLocation + binMid,
        expectedLocation,
        distance,
        ignoreLocation
      });

      if (score <= currentThreshold) {
        binMin = binMid;
      } else {
        binMax = binMid;
      }

      binMid = Math.floor((binMax - binMin) / 2 + binMin);
    }

    // Use the result from this iteration as the maximum for the next.
    binMax = binMid;

    let start = Math.max(1, expectedLocation - binMid + 1);
    let finish = findAllMatches
      ? textLen
      : Math.min(expectedLocation + binMid, textLen) + patternLen;

    // Initialize the bit array
    let bitArr = Array(finish + 2);

    bitArr[finish + 1] = (1 << i) - 1;

    for (let j = finish; j >= start; j -= 1) {
      let currentLocation = j - 1;
      let charMatch = patternAlphabet[text.charAt(currentLocation)];

      if (computeMatches) {
        // Speed up: quick bool to int conversion (i.e, `charMatch ? 1 : 0`)
        matchMask[currentLocation] = +!!charMatch;
      }

      // First pass: exact match
      bitArr[j] = ((bitArr[j + 1] << 1) | 1) & charMatch;

      // Subsequent passes: fuzzy match
      if (i) {
        bitArr[j] |=
          ((lastBitArr[j + 1] | lastBitArr[j]) << 1) | 1 | lastBitArr[j + 1];
      }

      if (bitArr[j] & mask) {
        finalScore = computeScore$1(pattern, {
          errors: i,
          currentLocation,
          expectedLocation,
          distance,
          ignoreLocation
        });

        // This match will almost certainly be better than any existing match.
        // But check anyway.
        if (finalScore <= currentThreshold) {
          // Indeed it is
          currentThreshold = finalScore;
          bestLocation = currentLocation;

          // Already passed `loc`, downhill from here on in.
          if (bestLocation <= expectedLocation) {
            break
          }

          // When passing `bestLocation`, don't exceed our current distance from `expectedLocation`.
          start = Math.max(1, 2 * expectedLocation - bestLocation);
        }
      }
    }

    // No hope for a (better) match at greater error levels.
    const score = computeScore$1(pattern, {
      errors: i + 1,
      currentLocation: expectedLocation,
      expectedLocation,
      distance,
      ignoreLocation
    });

    if (score > currentThreshold) {
      break
    }

    lastBitArr = bitArr;
  }

  const result = {
    isMatch: bestLocation >= 0,
    // Count exact matches (those with a score of 0) to be "almost" exact
    score: Math.max(0.001, finalScore)
  };

  if (computeMatches) {
    const indices = convertMaskToIndices(matchMask, minMatchCharLength);
    if (!indices.length) {
      result.isMatch = false;
    } else if (includeMatches) {
      result.indices = indices;
    }
  }

  return result
}

function createPatternAlphabet(pattern) {
  let mask = {};

  for (let i = 0, len = pattern.length; i < len; i += 1) {
    const char = pattern.charAt(i);
    mask[char] = (mask[char] || 0) | (1 << (len - i - 1));
  }

  return mask
}

class BitapSearch {
  constructor(
    pattern,
    {
      location = Config.location,
      threshold = Config.threshold,
      distance = Config.distance,
      includeMatches = Config.includeMatches,
      findAllMatches = Config.findAllMatches,
      minMatchCharLength = Config.minMatchCharLength,
      isCaseSensitive = Config.isCaseSensitive,
      ignoreLocation = Config.ignoreLocation
    } = {}
  ) {
    this.options = {
      location,
      threshold,
      distance,
      includeMatches,
      findAllMatches,
      minMatchCharLength,
      isCaseSensitive,
      ignoreLocation
    };

    this.pattern = isCaseSensitive ? pattern : pattern.toLowerCase();

    this.chunks = [];

    if (!this.pattern.length) {
      return
    }

    const addChunk = (pattern, startIndex) => {
      this.chunks.push({
        pattern,
        alphabet: createPatternAlphabet(pattern),
        startIndex
      });
    };

    const len = this.pattern.length;

    if (len > MAX_BITS) {
      let i = 0;
      const remainder = len % MAX_BITS;
      const end = len - remainder;

      while (i < end) {
        addChunk(this.pattern.substr(i, MAX_BITS), i);
        i += MAX_BITS;
      }

      if (remainder) {
        const startIndex = len - MAX_BITS;
        addChunk(this.pattern.substr(startIndex), startIndex);
      }
    } else {
      addChunk(this.pattern, 0);
    }
  }

  searchIn(text) {
    const { isCaseSensitive, includeMatches } = this.options;

    if (!isCaseSensitive) {
      text = text.toLowerCase();
    }

    // Exact match
    if (this.pattern === text) {
      let result = {
        isMatch: true,
        score: 0
      };

      if (includeMatches) {
        result.indices = [[0, text.length - 1]];
      }

      return result
    }

    // Otherwise, use Bitap algorithm
    const {
      location,
      distance,
      threshold,
      findAllMatches,
      minMatchCharLength,
      ignoreLocation
    } = this.options;

    let allIndices = [];
    let totalScore = 0;
    let hasMatches = false;

    this.chunks.forEach(({ pattern, alphabet, startIndex }) => {
      const { isMatch, score, indices } = search(text, pattern, alphabet, {
        location: location + startIndex,
        distance,
        threshold,
        findAllMatches,
        minMatchCharLength,
        includeMatches,
        ignoreLocation
      });

      if (isMatch) {
        hasMatches = true;
      }

      totalScore += score;

      if (isMatch && indices) {
        allIndices = [...allIndices, ...indices];
      }
    });

    let result = {
      isMatch: hasMatches,
      score: hasMatches ? totalScore / this.chunks.length : 1
    };

    if (hasMatches && includeMatches) {
      result.indices = allIndices;
    }

    return result
  }
}

class BaseMatch {
  constructor(pattern) {
    this.pattern = pattern;
  }
  static isMultiMatch(pattern) {
    return getMatch(pattern, this.multiRegex)
  }
  static isSingleMatch(pattern) {
    return getMatch(pattern, this.singleRegex)
  }
  search(/*text*/) {}
}

function getMatch(pattern, exp) {
  const matches = pattern.match(exp);
  return matches ? matches[1] : null
}

// Token: 'file

class ExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'exact'
  }
  static get multiRegex() {
    return /^="(.*)"$/
  }
  static get singleRegex() {
    return /^=(.*)$/
  }
  search(text) {
    const isMatch = text === this.pattern;

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [0, this.pattern.length - 1]
    }
  }
}

// Token: !fire

class InverseExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'inverse-exact'
  }
  static get multiRegex() {
    return /^!"(.*)"$/
  }
  static get singleRegex() {
    return /^!(.*)$/
  }
  search(text) {
    const index = text.indexOf(this.pattern);
    const isMatch = index === -1;

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [0, text.length - 1]
    }
  }
}

// Token: ^file

class PrefixExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'prefix-exact'
  }
  static get multiRegex() {
    return /^\^"(.*)"$/
  }
  static get singleRegex() {
    return /^\^(.*)$/
  }
  search(text) {
    const isMatch = text.startsWith(this.pattern);

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [0, this.pattern.length - 1]
    }
  }
}

// Token: !^fire

class InversePrefixExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'inverse-prefix-exact'
  }
  static get multiRegex() {
    return /^!\^"(.*)"$/
  }
  static get singleRegex() {
    return /^!\^(.*)$/
  }
  search(text) {
    const isMatch = !text.startsWith(this.pattern);

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [0, text.length - 1]
    }
  }
}

// Token: .file$

class SuffixExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'suffix-exact'
  }
  static get multiRegex() {
    return /^"(.*)"\$$/
  }
  static get singleRegex() {
    return /^(.*)\$$/
  }
  search(text) {
    const isMatch = text.endsWith(this.pattern);

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [text.length - this.pattern.length, text.length - 1]
    }
  }
}

// Token: !.file$

class InverseSuffixExactMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'inverse-suffix-exact'
  }
  static get multiRegex() {
    return /^!"(.*)"\$$/
  }
  static get singleRegex() {
    return /^!(.*)\$$/
  }
  search(text) {
    const isMatch = !text.endsWith(this.pattern);
    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices: [0, text.length - 1]
    }
  }
}

class FuzzyMatch extends BaseMatch {
  constructor(
    pattern,
    {
      location = Config.location,
      threshold = Config.threshold,
      distance = Config.distance,
      includeMatches = Config.includeMatches,
      findAllMatches = Config.findAllMatches,
      minMatchCharLength = Config.minMatchCharLength,
      isCaseSensitive = Config.isCaseSensitive,
      ignoreLocation = Config.ignoreLocation
    } = {}
  ) {
    super(pattern);
    this._bitapSearch = new BitapSearch(pattern, {
      location,
      threshold,
      distance,
      includeMatches,
      findAllMatches,
      minMatchCharLength,
      isCaseSensitive,
      ignoreLocation
    });
  }
  static get type() {
    return 'fuzzy'
  }
  static get multiRegex() {
    return /^"(.*)"$/
  }
  static get singleRegex() {
    return /^(.*)$/
  }
  search(text) {
    return this._bitapSearch.searchIn(text)
  }
}

// Token: 'file

class IncludeMatch extends BaseMatch {
  constructor(pattern) {
    super(pattern);
  }
  static get type() {
    return 'include'
  }
  static get multiRegex() {
    return /^'"(.*)"$/
  }
  static get singleRegex() {
    return /^'(.*)$/
  }
  search(text) {
    let location = 0;
    let index;

    const indices = [];
    const patternLen = this.pattern.length;

    // Get all exact matches
    while ((index = text.indexOf(this.pattern, location)) > -1) {
      location = index + patternLen;
      indices.push([index, location - 1]);
    }

    const isMatch = !!indices.length;

    return {
      isMatch,
      score: isMatch ? 0 : 1,
      indices
    }
  }
}

// ❗Order is important. DO NOT CHANGE.
const searchers = [
  ExactMatch,
  IncludeMatch,
  PrefixExactMatch,
  InversePrefixExactMatch,
  InverseSuffixExactMatch,
  SuffixExactMatch,
  InverseExactMatch,
  FuzzyMatch
];

const searchersLen = searchers.length;

// Regex to split by spaces, but keep anything in quotes together
const SPACE_RE = / +(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/;
const OR_TOKEN = '|';

// Return a 2D array representation of the query, for simpler parsing.
// Example:
// "^core go$ | rb$ | py$ xy$" => [["^core", "go$"], ["rb$"], ["py$", "xy$"]]
function parseQuery(pattern, options = {}) {
  return pattern.split(OR_TOKEN).map((item) => {
    let query = item
      .trim()
      .split(SPACE_RE)
      .filter((item) => item && !!item.trim());

    let results = [];
    for (let i = 0, len = query.length; i < len; i += 1) {
      const queryItem = query[i];

      // 1. Handle multiple query match (i.e, once that are quoted, like `"hello world"`)
      let found = false;
      let idx = -1;
      while (!found && ++idx < searchersLen) {
        const searcher = searchers[idx];
        let token = searcher.isMultiMatch(queryItem);
        if (token) {
          results.push(new searcher(token, options));
          found = true;
        }
      }

      if (found) {
        continue
      }

      // 2. Handle single query matches (i.e, once that are *not* quoted)
      idx = -1;
      while (++idx < searchersLen) {
        const searcher = searchers[idx];
        let token = searcher.isSingleMatch(queryItem);
        if (token) {
          results.push(new searcher(token, options));
          break
        }
      }
    }

    return results
  })
}

// These extended matchers can return an array of matches, as opposed
// to a singl match
const MultiMatchSet = new Set([FuzzyMatch.type, IncludeMatch.type]);

/**
 * Command-like searching
 * ======================
 *
 * Given multiple search terms delimited by spaces.e.g. `^jscript .python$ ruby !java`,
 * search in a given text.
 *
 * Search syntax:
 *
 * | Token       | Match type                 | Description                            |
 * | ----------- | -------------------------- | -------------------------------------- |
 * | `jscript`   | fuzzy-match                | Items that fuzzy match `jscript`       |
 * | `=scheme`   | exact-match                | Items that are `scheme`                |
 * | `'python`   | include-match              | Items that include `python`            |
 * | `!ruby`     | inverse-exact-match        | Items that do not include `ruby`       |
 * | `^java`     | prefix-exact-match         | Items that start with `java`           |
 * | `!^earlang` | inverse-prefix-exact-match | Items that do not start with `earlang` |
 * | `.js$`      | suffix-exact-match         | Items that end with `.js`              |
 * | `!.go$`     | inverse-suffix-exact-match | Items that do not end with `.go`       |
 *
 * A single pipe character acts as an OR operator. For example, the following
 * query matches entries that start with `core` and end with either`go`, `rb`,
 * or`py`.
 *
 * ```
 * ^core go$ | rb$ | py$
 * ```
 */
class ExtendedSearch {
  constructor(
    pattern,
    {
      isCaseSensitive = Config.isCaseSensitive,
      includeMatches = Config.includeMatches,
      minMatchCharLength = Config.minMatchCharLength,
      ignoreLocation = Config.ignoreLocation,
      findAllMatches = Config.findAllMatches,
      location = Config.location,
      threshold = Config.threshold,
      distance = Config.distance
    } = {}
  ) {
    this.query = null;
    this.options = {
      isCaseSensitive,
      includeMatches,
      minMatchCharLength,
      findAllMatches,
      ignoreLocation,
      location,
      threshold,
      distance
    };

    this.pattern = isCaseSensitive ? pattern : pattern.toLowerCase();
    this.query = parseQuery(this.pattern, this.options);
  }

  static condition(_, options) {
    return options.useExtendedSearch
  }

  searchIn(text) {
    const query = this.query;

    if (!query) {
      return {
        isMatch: false,
        score: 1
      }
    }

    const { includeMatches, isCaseSensitive } = this.options;

    text = isCaseSensitive ? text : text.toLowerCase();

    let numMatches = 0;
    let allIndices = [];
    let totalScore = 0;

    // ORs
    for (let i = 0, qLen = query.length; i < qLen; i += 1) {
      const searchers = query[i];

      // Reset indices
      allIndices.length = 0;
      numMatches = 0;

      // ANDs
      for (let j = 0, pLen = searchers.length; j < pLen; j += 1) {
        const searcher = searchers[j];
        const { isMatch, indices, score } = searcher.search(text);

        if (isMatch) {
          numMatches += 1;
          totalScore += score;
          if (includeMatches) {
            const type = searcher.constructor.type;
            if (MultiMatchSet.has(type)) {
              allIndices = [...allIndices, ...indices];
            } else {
              allIndices.push(indices);
            }
          }
        } else {
          totalScore = 0;
          numMatches = 0;
          allIndices.length = 0;
          break
        }
      }

      // OR condition, so if TRUE, return
      if (numMatches) {
        let result = {
          isMatch: true,
          score: totalScore / numMatches
        };

        if (includeMatches) {
          result.indices = allIndices;
        }

        return result
      }
    }

    // Nothing was matched
    return {
      isMatch: false,
      score: 1
    }
  }
}

const registeredSearchers = [];

function register(...args) {
  registeredSearchers.push(...args);
}

function createSearcher(pattern, options) {
  for (let i = 0, len = registeredSearchers.length; i < len; i += 1) {
    let searcherClass = registeredSearchers[i];
    if (searcherClass.condition(pattern, options)) {
      return new searcherClass(pattern, options)
    }
  }

  return new BitapSearch(pattern, options)
}

const LogicalOperator = {
  AND: '$and',
  OR: '$or'
};

const KeyType = {
  PATH: '$path',
  PATTERN: '$val'
};

const isExpression = (query) =>
  !!(query[LogicalOperator.AND] || query[LogicalOperator.OR]);

const isPath = (query) => !!query[KeyType.PATH];

const isLeaf = (query) =>
  !isArray(query) && isObject(query) && !isExpression(query);

const convertToExplicit = (query) => ({
  [LogicalOperator.AND]: Object.keys(query).map((key) => ({
    [key]: query[key]
  }))
});

// When `auto` is `true`, the parse function will infer and initialize and add
// the appropriate `Searcher` instance
function parse(query, options, { auto = true } = {}) {
  const next = (query) => {
    let keys = Object.keys(query);

    const isQueryPath = isPath(query);

    if (!isQueryPath && keys.length > 1 && !isExpression(query)) {
      return next(convertToExplicit(query))
    }

    if (isLeaf(query)) {
      const key = isQueryPath ? query[KeyType.PATH] : keys[0];

      const pattern = isQueryPath ? query[KeyType.PATTERN] : query[key];

      if (!isString(pattern)) {
        throw new Error(LOGICAL_SEARCH_INVALID_QUERY_FOR_KEY(key))
      }

      const obj = {
        keyId: createKeyId(key),
        pattern
      };

      if (auto) {
        obj.searcher = createSearcher(pattern, options);
      }

      return obj
    }

    let node = {
      children: [],
      operator: keys[0]
    };

    keys.forEach((key) => {
      const value = query[key];

      if (isArray(value)) {
        value.forEach((item) => {
          node.children.push(next(item));
        });
      }
    });

    return node
  };

  if (!isExpression(query)) {
    query = convertToExplicit(query);
  }

  return next(query)
}

// Practical scoring function
function computeScore(
  results,
  { ignoreFieldNorm = Config.ignoreFieldNorm }
) {
  results.forEach((result) => {
    let totalScore = 1;

    result.matches.forEach(({ key, norm, score }) => {
      const weight = key ? key.weight : null;

      totalScore *= Math.pow(
        score === 0 && weight ? Number.EPSILON : score,
        (weight || 1) * (ignoreFieldNorm ? 1 : norm)
      );
    });

    result.score = totalScore;
  });
}

function transformMatches(result, data) {
  const matches = result.matches;
  data.matches = [];

  if (!isDefined(matches)) {
    return
  }

  matches.forEach((match) => {
    if (!isDefined(match.indices) || !match.indices.length) {
      return
    }

    const { indices, value } = match;

    let obj = {
      indices,
      value
    };

    if (match.key) {
      obj.key = match.key.src;
    }

    if (match.idx > -1) {
      obj.refIndex = match.idx;
    }

    data.matches.push(obj);
  });
}

function transformScore(result, data) {
  data.score = result.score;
}

function format(
  results,
  docs,
  {
    includeMatches = Config.includeMatches,
    includeScore = Config.includeScore
  } = {}
) {
  const transformers = [];

  if (includeMatches) transformers.push(transformMatches);
  if (includeScore) transformers.push(transformScore);

  return results.map((result) => {
    const { idx } = result;

    const data = {
      item: docs[idx],
      refIndex: idx
    };

    if (transformers.length) {
      transformers.forEach((transformer) => {
        transformer(result, data);
      });
    }

    return data
  })
}

class Fuse {
  constructor(docs, options = {}, index) {
    this.options = { ...Config, ...options };

    if (
      this.options.useExtendedSearch &&
      !true
    ) {
      throw new Error(EXTENDED_SEARCH_UNAVAILABLE)
    }

    this._keyStore = new KeyStore(this.options.keys);

    this.setCollection(docs, index);
  }

  setCollection(docs, index) {
    this._docs = docs;

    if (index && !(index instanceof FuseIndex)) {
      throw new Error(INCORRECT_INDEX_TYPE)
    }

    this._myIndex =
      index ||
      createIndex(this.options.keys, this._docs, {
        getFn: this.options.getFn,
        fieldNormWeight: this.options.fieldNormWeight
      });
  }

  add(doc) {
    if (!isDefined(doc)) {
      return
    }

    this._docs.push(doc);
    this._myIndex.add(doc);
  }

  remove(predicate = (/* doc, idx */) => false) {
    const results = [];

    for (let i = 0, len = this._docs.length; i < len; i += 1) {
      const doc = this._docs[i];
      if (predicate(doc, i)) {
        this.removeAt(i);
        i -= 1;
        len -= 1;

        results.push(doc);
      }
    }

    return results
  }

  removeAt(idx) {
    this._docs.splice(idx, 1);
    this._myIndex.removeAt(idx);
  }

  getIndex() {
    return this._myIndex
  }

  search(query, { limit = -1 } = {}) {
    const {
      includeMatches,
      includeScore,
      shouldSort,
      sortFn,
      ignoreFieldNorm
    } = this.options;

    let results = isString(query)
      ? isString(this._docs[0])
        ? this._searchStringList(query)
        : this._searchObjectList(query)
      : this._searchLogical(query);

    computeScore(results, { ignoreFieldNorm });

    if (shouldSort) {
      results.sort(sortFn);
    }

    if (isNumber(limit) && limit > -1) {
      results = results.slice(0, limit);
    }

    return format(results, this._docs, {
      includeMatches,
      includeScore
    })
  }

  _searchStringList(query) {
    const searcher = createSearcher(query, this.options);
    const { records } = this._myIndex;
    const results = [];

    // Iterate over every string in the index
    records.forEach(({ v: text, i: idx, n: norm }) => {
      if (!isDefined(text)) {
        return
      }

      const { isMatch, score, indices } = searcher.searchIn(text);

      if (isMatch) {
        results.push({
          item: text,
          idx,
          matches: [{ score, value: text, norm, indices }]
        });
      }
    });

    return results
  }

  _searchLogical(query) {

    const expression = parse(query, this.options);

    const evaluate = (node, item, idx) => {
      if (!node.children) {
        const { keyId, searcher } = node;

        const matches = this._findMatches({
          key: this._keyStore.get(keyId),
          value: this._myIndex.getValueForItemAtKeyId(item, keyId),
          searcher
        });

        if (matches && matches.length) {
          return [
            {
              idx,
              item,
              matches
            }
          ]
        }

        return []
      }

      const res = [];
      for (let i = 0, len = node.children.length; i < len; i += 1) {
        const child = node.children[i];
        const result = evaluate(child, item, idx);
        if (result.length) {
          res.push(...result);
        } else if (node.operator === LogicalOperator.AND) {
          return []
        }
      }
      return res
    };

    const records = this._myIndex.records;
    const resultMap = {};
    const results = [];

    records.forEach(({ $: item, i: idx }) => {
      if (isDefined(item)) {
        let expResults = evaluate(expression, item, idx);

        if (expResults.length) {
          // Dedupe when adding
          if (!resultMap[idx]) {
            resultMap[idx] = { idx, item, matches: [] };
            results.push(resultMap[idx]);
          }
          expResults.forEach(({ matches }) => {
            resultMap[idx].matches.push(...matches);
          });
        }
      }
    });

    return results
  }

  _searchObjectList(query) {
    const searcher = createSearcher(query, this.options);
    const { keys, records } = this._myIndex;
    const results = [];

    // List is Array<Object>
    records.forEach(({ $: item, i: idx }) => {
      if (!isDefined(item)) {
        return
      }

      let matches = [];

      // Iterate over every key (i.e, path), and fetch the value at that key
      keys.forEach((key, keyIndex) => {
        matches.push(
          ...this._findMatches({
            key,
            value: item[keyIndex],
            searcher
          })
        );
      });

      if (matches.length) {
        results.push({
          idx,
          item,
          matches
        });
      }
    });

    return results
  }
  _findMatches({ key, value, searcher }) {
    if (!isDefined(value)) {
      return []
    }

    let matches = [];

    if (isArray(value)) {
      value.forEach(({ v: text, i: idx, n: norm }) => {
        if (!isDefined(text)) {
          return
        }

        const { isMatch, score, indices } = searcher.searchIn(text);

        if (isMatch) {
          matches.push({
            score,
            key,
            value: text,
            idx,
            norm,
            indices
          });
        }
      });
    } else {
      const { v: text, n: norm } = value;

      const { isMatch, score, indices } = searcher.searchIn(text);

      if (isMatch) {
        matches.push({ score, key, value: text, norm, indices });
      }
    }

    return matches
  }
}

Fuse.version = '7.0.0';
Fuse.createIndex = createIndex;
Fuse.parseIndex = parseIndex;
Fuse.config = Config;

{
  Fuse.parseQuery = parse;
}

{
  register(ExtendedSearch);
}

var lib$1 = {exports: {}};

var _default$1 = {};

var lib = {exports: {}};

var _default = {};

/**
 * cssfilter
 *
 * @author 老雷<leizongmin@gmail.com>
 */

function getDefaultWhiteList$1 () {
  // 白名单值说明：
  // true: 允许该属性
  // Function: function (val) { } 返回true表示允许该属性，其他值均表示不允许
  // RegExp: regexp.test(val) 返回true表示允许该属性，其他值均表示不允许
  // 除上面列出的值外均表示不允许
  var whiteList = {};

  whiteList['align-content'] = false; // default: auto
  whiteList['align-items'] = false; // default: auto
  whiteList['align-self'] = false; // default: auto
  whiteList['alignment-adjust'] = false; // default: auto
  whiteList['alignment-baseline'] = false; // default: baseline
  whiteList['all'] = false; // default: depending on individual properties
  whiteList['anchor-point'] = false; // default: none
  whiteList['animation'] = false; // default: depending on individual properties
  whiteList['animation-delay'] = false; // default: 0
  whiteList['animation-direction'] = false; // default: normal
  whiteList['animation-duration'] = false; // default: 0
  whiteList['animation-fill-mode'] = false; // default: none
  whiteList['animation-iteration-count'] = false; // default: 1
  whiteList['animation-name'] = false; // default: none
  whiteList['animation-play-state'] = false; // default: running
  whiteList['animation-timing-function'] = false; // default: ease
  whiteList['azimuth'] = false; // default: center
  whiteList['backface-visibility'] = false; // default: visible
  whiteList['background'] = true; // default: depending on individual properties
  whiteList['background-attachment'] = true; // default: scroll
  whiteList['background-clip'] = true; // default: border-box
  whiteList['background-color'] = true; // default: transparent
  whiteList['background-image'] = true; // default: none
  whiteList['background-origin'] = true; // default: padding-box
  whiteList['background-position'] = true; // default: 0% 0%
  whiteList['background-repeat'] = true; // default: repeat
  whiteList['background-size'] = true; // default: auto
  whiteList['baseline-shift'] = false; // default: baseline
  whiteList['binding'] = false; // default: none
  whiteList['bleed'] = false; // default: 6pt
  whiteList['bookmark-label'] = false; // default: content()
  whiteList['bookmark-level'] = false; // default: none
  whiteList['bookmark-state'] = false; // default: open
  whiteList['border'] = true; // default: depending on individual properties
  whiteList['border-bottom'] = true; // default: depending on individual properties
  whiteList['border-bottom-color'] = true; // default: current color
  whiteList['border-bottom-left-radius'] = true; // default: 0
  whiteList['border-bottom-right-radius'] = true; // default: 0
  whiteList['border-bottom-style'] = true; // default: none
  whiteList['border-bottom-width'] = true; // default: medium
  whiteList['border-collapse'] = true; // default: separate
  whiteList['border-color'] = true; // default: depending on individual properties
  whiteList['border-image'] = true; // default: none
  whiteList['border-image-outset'] = true; // default: 0
  whiteList['border-image-repeat'] = true; // default: stretch
  whiteList['border-image-slice'] = true; // default: 100%
  whiteList['border-image-source'] = true; // default: none
  whiteList['border-image-width'] = true; // default: 1
  whiteList['border-left'] = true; // default: depending on individual properties
  whiteList['border-left-color'] = true; // default: current color
  whiteList['border-left-style'] = true; // default: none
  whiteList['border-left-width'] = true; // default: medium
  whiteList['border-radius'] = true; // default: 0
  whiteList['border-right'] = true; // default: depending on individual properties
  whiteList['border-right-color'] = true; // default: current color
  whiteList['border-right-style'] = true; // default: none
  whiteList['border-right-width'] = true; // default: medium
  whiteList['border-spacing'] = true; // default: 0
  whiteList['border-style'] = true; // default: depending on individual properties
  whiteList['border-top'] = true; // default: depending on individual properties
  whiteList['border-top-color'] = true; // default: current color
  whiteList['border-top-left-radius'] = true; // default: 0
  whiteList['border-top-right-radius'] = true; // default: 0
  whiteList['border-top-style'] = true; // default: none
  whiteList['border-top-width'] = true; // default: medium
  whiteList['border-width'] = true; // default: depending on individual properties
  whiteList['bottom'] = false; // default: auto
  whiteList['box-decoration-break'] = true; // default: slice
  whiteList['box-shadow'] = true; // default: none
  whiteList['box-sizing'] = true; // default: content-box
  whiteList['box-snap'] = true; // default: none
  whiteList['box-suppress'] = true; // default: show
  whiteList['break-after'] = true; // default: auto
  whiteList['break-before'] = true; // default: auto
  whiteList['break-inside'] = true; // default: auto
  whiteList['caption-side'] = false; // default: top
  whiteList['chains'] = false; // default: none
  whiteList['clear'] = true; // default: none
  whiteList['clip'] = false; // default: auto
  whiteList['clip-path'] = false; // default: none
  whiteList['clip-rule'] = false; // default: nonzero
  whiteList['color'] = true; // default: implementation dependent
  whiteList['color-interpolation-filters'] = true; // default: auto
  whiteList['column-count'] = false; // default: auto
  whiteList['column-fill'] = false; // default: balance
  whiteList['column-gap'] = false; // default: normal
  whiteList['column-rule'] = false; // default: depending on individual properties
  whiteList['column-rule-color'] = false; // default: current color
  whiteList['column-rule-style'] = false; // default: medium
  whiteList['column-rule-width'] = false; // default: medium
  whiteList['column-span'] = false; // default: none
  whiteList['column-width'] = false; // default: auto
  whiteList['columns'] = false; // default: depending on individual properties
  whiteList['contain'] = false; // default: none
  whiteList['content'] = false; // default: normal
  whiteList['counter-increment'] = false; // default: none
  whiteList['counter-reset'] = false; // default: none
  whiteList['counter-set'] = false; // default: none
  whiteList['crop'] = false; // default: auto
  whiteList['cue'] = false; // default: depending on individual properties
  whiteList['cue-after'] = false; // default: none
  whiteList['cue-before'] = false; // default: none
  whiteList['cursor'] = false; // default: auto
  whiteList['direction'] = false; // default: ltr
  whiteList['display'] = true; // default: depending on individual properties
  whiteList['display-inside'] = true; // default: auto
  whiteList['display-list'] = true; // default: none
  whiteList['display-outside'] = true; // default: inline-level
  whiteList['dominant-baseline'] = false; // default: auto
  whiteList['elevation'] = false; // default: level
  whiteList['empty-cells'] = false; // default: show
  whiteList['filter'] = false; // default: none
  whiteList['flex'] = false; // default: depending on individual properties
  whiteList['flex-basis'] = false; // default: auto
  whiteList['flex-direction'] = false; // default: row
  whiteList['flex-flow'] = false; // default: depending on individual properties
  whiteList['flex-grow'] = false; // default: 0
  whiteList['flex-shrink'] = false; // default: 1
  whiteList['flex-wrap'] = false; // default: nowrap
  whiteList['float'] = false; // default: none
  whiteList['float-offset'] = false; // default: 0 0
  whiteList['flood-color'] = false; // default: black
  whiteList['flood-opacity'] = false; // default: 1
  whiteList['flow-from'] = false; // default: none
  whiteList['flow-into'] = false; // default: none
  whiteList['font'] = true; // default: depending on individual properties
  whiteList['font-family'] = true; // default: implementation dependent
  whiteList['font-feature-settings'] = true; // default: normal
  whiteList['font-kerning'] = true; // default: auto
  whiteList['font-language-override'] = true; // default: normal
  whiteList['font-size'] = true; // default: medium
  whiteList['font-size-adjust'] = true; // default: none
  whiteList['font-stretch'] = true; // default: normal
  whiteList['font-style'] = true; // default: normal
  whiteList['font-synthesis'] = true; // default: weight style
  whiteList['font-variant'] = true; // default: normal
  whiteList['font-variant-alternates'] = true; // default: normal
  whiteList['font-variant-caps'] = true; // default: normal
  whiteList['font-variant-east-asian'] = true; // default: normal
  whiteList['font-variant-ligatures'] = true; // default: normal
  whiteList['font-variant-numeric'] = true; // default: normal
  whiteList['font-variant-position'] = true; // default: normal
  whiteList['font-weight'] = true; // default: normal
  whiteList['grid'] = false; // default: depending on individual properties
  whiteList['grid-area'] = false; // default: depending on individual properties
  whiteList['grid-auto-columns'] = false; // default: auto
  whiteList['grid-auto-flow'] = false; // default: none
  whiteList['grid-auto-rows'] = false; // default: auto
  whiteList['grid-column'] = false; // default: depending on individual properties
  whiteList['grid-column-end'] = false; // default: auto
  whiteList['grid-column-start'] = false; // default: auto
  whiteList['grid-row'] = false; // default: depending on individual properties
  whiteList['grid-row-end'] = false; // default: auto
  whiteList['grid-row-start'] = false; // default: auto
  whiteList['grid-template'] = false; // default: depending on individual properties
  whiteList['grid-template-areas'] = false; // default: none
  whiteList['grid-template-columns'] = false; // default: none
  whiteList['grid-template-rows'] = false; // default: none
  whiteList['hanging-punctuation'] = false; // default: none
  whiteList['height'] = true; // default: auto
  whiteList['hyphens'] = false; // default: manual
  whiteList['icon'] = false; // default: auto
  whiteList['image-orientation'] = false; // default: auto
  whiteList['image-resolution'] = false; // default: normal
  whiteList['ime-mode'] = false; // default: auto
  whiteList['initial-letters'] = false; // default: normal
  whiteList['inline-box-align'] = false; // default: last
  whiteList['justify-content'] = false; // default: auto
  whiteList['justify-items'] = false; // default: auto
  whiteList['justify-self'] = false; // default: auto
  whiteList['left'] = false; // default: auto
  whiteList['letter-spacing'] = true; // default: normal
  whiteList['lighting-color'] = true; // default: white
  whiteList['line-box-contain'] = false; // default: block inline replaced
  whiteList['line-break'] = false; // default: auto
  whiteList['line-grid'] = false; // default: match-parent
  whiteList['line-height'] = false; // default: normal
  whiteList['line-snap'] = false; // default: none
  whiteList['line-stacking'] = false; // default: depending on individual properties
  whiteList['line-stacking-ruby'] = false; // default: exclude-ruby
  whiteList['line-stacking-shift'] = false; // default: consider-shifts
  whiteList['line-stacking-strategy'] = false; // default: inline-line-height
  whiteList['list-style'] = true; // default: depending on individual properties
  whiteList['list-style-image'] = true; // default: none
  whiteList['list-style-position'] = true; // default: outside
  whiteList['list-style-type'] = true; // default: disc
  whiteList['margin'] = true; // default: depending on individual properties
  whiteList['margin-bottom'] = true; // default: 0
  whiteList['margin-left'] = true; // default: 0
  whiteList['margin-right'] = true; // default: 0
  whiteList['margin-top'] = true; // default: 0
  whiteList['marker-offset'] = false; // default: auto
  whiteList['marker-side'] = false; // default: list-item
  whiteList['marks'] = false; // default: none
  whiteList['mask'] = false; // default: border-box
  whiteList['mask-box'] = false; // default: see individual properties
  whiteList['mask-box-outset'] = false; // default: 0
  whiteList['mask-box-repeat'] = false; // default: stretch
  whiteList['mask-box-slice'] = false; // default: 0 fill
  whiteList['mask-box-source'] = false; // default: none
  whiteList['mask-box-width'] = false; // default: auto
  whiteList['mask-clip'] = false; // default: border-box
  whiteList['mask-image'] = false; // default: none
  whiteList['mask-origin'] = false; // default: border-box
  whiteList['mask-position'] = false; // default: center
  whiteList['mask-repeat'] = false; // default: no-repeat
  whiteList['mask-size'] = false; // default: border-box
  whiteList['mask-source-type'] = false; // default: auto
  whiteList['mask-type'] = false; // default: luminance
  whiteList['max-height'] = true; // default: none
  whiteList['max-lines'] = false; // default: none
  whiteList['max-width'] = true; // default: none
  whiteList['min-height'] = true; // default: 0
  whiteList['min-width'] = true; // default: 0
  whiteList['move-to'] = false; // default: normal
  whiteList['nav-down'] = false; // default: auto
  whiteList['nav-index'] = false; // default: auto
  whiteList['nav-left'] = false; // default: auto
  whiteList['nav-right'] = false; // default: auto
  whiteList['nav-up'] = false; // default: auto
  whiteList['object-fit'] = false; // default: fill
  whiteList['object-position'] = false; // default: 50% 50%
  whiteList['opacity'] = false; // default: 1
  whiteList['order'] = false; // default: 0
  whiteList['orphans'] = false; // default: 2
  whiteList['outline'] = false; // default: depending on individual properties
  whiteList['outline-color'] = false; // default: invert
  whiteList['outline-offset'] = false; // default: 0
  whiteList['outline-style'] = false; // default: none
  whiteList['outline-width'] = false; // default: medium
  whiteList['overflow'] = false; // default: depending on individual properties
  whiteList['overflow-wrap'] = false; // default: normal
  whiteList['overflow-x'] = false; // default: visible
  whiteList['overflow-y'] = false; // default: visible
  whiteList['padding'] = true; // default: depending on individual properties
  whiteList['padding-bottom'] = true; // default: 0
  whiteList['padding-left'] = true; // default: 0
  whiteList['padding-right'] = true; // default: 0
  whiteList['padding-top'] = true; // default: 0
  whiteList['page'] = false; // default: auto
  whiteList['page-break-after'] = false; // default: auto
  whiteList['page-break-before'] = false; // default: auto
  whiteList['page-break-inside'] = false; // default: auto
  whiteList['page-policy'] = false; // default: start
  whiteList['pause'] = false; // default: implementation dependent
  whiteList['pause-after'] = false; // default: implementation dependent
  whiteList['pause-before'] = false; // default: implementation dependent
  whiteList['perspective'] = false; // default: none
  whiteList['perspective-origin'] = false; // default: 50% 50%
  whiteList['pitch'] = false; // default: medium
  whiteList['pitch-range'] = false; // default: 50
  whiteList['play-during'] = false; // default: auto
  whiteList['position'] = false; // default: static
  whiteList['presentation-level'] = false; // default: 0
  whiteList['quotes'] = false; // default: text
  whiteList['region-fragment'] = false; // default: auto
  whiteList['resize'] = false; // default: none
  whiteList['rest'] = false; // default: depending on individual properties
  whiteList['rest-after'] = false; // default: none
  whiteList['rest-before'] = false; // default: none
  whiteList['richness'] = false; // default: 50
  whiteList['right'] = false; // default: auto
  whiteList['rotation'] = false; // default: 0
  whiteList['rotation-point'] = false; // default: 50% 50%
  whiteList['ruby-align'] = false; // default: auto
  whiteList['ruby-merge'] = false; // default: separate
  whiteList['ruby-position'] = false; // default: before
  whiteList['shape-image-threshold'] = false; // default: 0.0
  whiteList['shape-outside'] = false; // default: none
  whiteList['shape-margin'] = false; // default: 0
  whiteList['size'] = false; // default: auto
  whiteList['speak'] = false; // default: auto
  whiteList['speak-as'] = false; // default: normal
  whiteList['speak-header'] = false; // default: once
  whiteList['speak-numeral'] = false; // default: continuous
  whiteList['speak-punctuation'] = false; // default: none
  whiteList['speech-rate'] = false; // default: medium
  whiteList['stress'] = false; // default: 50
  whiteList['string-set'] = false; // default: none
  whiteList['tab-size'] = false; // default: 8
  whiteList['table-layout'] = false; // default: auto
  whiteList['text-align'] = true; // default: start
  whiteList['text-align-last'] = true; // default: auto
  whiteList['text-combine-upright'] = true; // default: none
  whiteList['text-decoration'] = true; // default: none
  whiteList['text-decoration-color'] = true; // default: currentColor
  whiteList['text-decoration-line'] = true; // default: none
  whiteList['text-decoration-skip'] = true; // default: objects
  whiteList['text-decoration-style'] = true; // default: solid
  whiteList['text-emphasis'] = true; // default: depending on individual properties
  whiteList['text-emphasis-color'] = true; // default: currentColor
  whiteList['text-emphasis-position'] = true; // default: over right
  whiteList['text-emphasis-style'] = true; // default: none
  whiteList['text-height'] = true; // default: auto
  whiteList['text-indent'] = true; // default: 0
  whiteList['text-justify'] = true; // default: auto
  whiteList['text-orientation'] = true; // default: mixed
  whiteList['text-overflow'] = true; // default: clip
  whiteList['text-shadow'] = true; // default: none
  whiteList['text-space-collapse'] = true; // default: collapse
  whiteList['text-transform'] = true; // default: none
  whiteList['text-underline-position'] = true; // default: auto
  whiteList['text-wrap'] = true; // default: normal
  whiteList['top'] = false; // default: auto
  whiteList['transform'] = false; // default: none
  whiteList['transform-origin'] = false; // default: 50% 50% 0
  whiteList['transform-style'] = false; // default: flat
  whiteList['transition'] = false; // default: depending on individual properties
  whiteList['transition-delay'] = false; // default: 0s
  whiteList['transition-duration'] = false; // default: 0s
  whiteList['transition-property'] = false; // default: all
  whiteList['transition-timing-function'] = false; // default: ease
  whiteList['unicode-bidi'] = false; // default: normal
  whiteList['vertical-align'] = false; // default: baseline
  whiteList['visibility'] = false; // default: visible
  whiteList['voice-balance'] = false; // default: center
  whiteList['voice-duration'] = false; // default: auto
  whiteList['voice-family'] = false; // default: implementation dependent
  whiteList['voice-pitch'] = false; // default: medium
  whiteList['voice-range'] = false; // default: medium
  whiteList['voice-rate'] = false; // default: normal
  whiteList['voice-stress'] = false; // default: normal
  whiteList['voice-volume'] = false; // default: medium
  whiteList['volume'] = false; // default: medium
  whiteList['white-space'] = false; // default: normal
  whiteList['widows'] = false; // default: 2
  whiteList['width'] = true; // default: auto
  whiteList['will-change'] = false; // default: auto
  whiteList['word-break'] = true; // default: normal
  whiteList['word-spacing'] = true; // default: normal
  whiteList['word-wrap'] = true; // default: normal
  whiteList['wrap-flow'] = false; // default: auto
  whiteList['wrap-through'] = false; // default: wrap
  whiteList['writing-mode'] = false; // default: horizontal-tb
  whiteList['z-index'] = false; // default: auto

  return whiteList;
}


/**
 * 匹配到白名单上的一个属性时
 *
 * @param {String} name
 * @param {String} value
 * @param {Object} options
 * @return {String}
 */
function onAttr (name, value, options) {
  // do nothing
}

/**
 * 匹配到不在白名单上的一个属性时
 *
 * @param {String} name
 * @param {String} value
 * @param {Object} options
 * @return {String}
 */
function onIgnoreAttr (name, value, options) {
  // do nothing
}

var REGEXP_URL_JAVASCRIPT = /javascript\s*\:/img;

/**
 * 过滤属性值
 *
 * @param {String} name
 * @param {String} value
 * @return {String}
 */
function safeAttrValue$1(name, value) {
  if (REGEXP_URL_JAVASCRIPT.test(value)) return '';
  return value;
}


_default.whiteList = getDefaultWhiteList$1();
_default.getDefaultWhiteList = getDefaultWhiteList$1;
_default.onAttr = onAttr;
_default.onIgnoreAttr = onIgnoreAttr;
_default.safeAttrValue = safeAttrValue$1;

var util$1 = {
  indexOf: function (arr, item) {
    var i, j;
    if (Array.prototype.indexOf) {
      return arr.indexOf(item);
    }
    for (i = 0, j = arr.length; i < j; i++) {
      if (arr[i] === item) {
        return i;
      }
    }
    return -1;
  },
  forEach: function (arr, fn, scope) {
    var i, j;
    if (Array.prototype.forEach) {
      return arr.forEach(fn, scope);
    }
    for (i = 0, j = arr.length; i < j; i++) {
      fn.call(scope, arr[i], i, arr);
    }
  },
  trim: function (str) {
    if (String.prototype.trim) {
      return str.trim();
    }
    return str.replace(/(^\s*)|(\s*$)/g, '');
  },
  trimRight: function (str) {
    if (String.prototype.trimRight) {
      return str.trimRight();
    }
    return str.replace(/(\s*$)/g, '');
  }
};

/**
 * cssfilter
 *
 * @author 老雷<leizongmin@gmail.com>
 */

var _$3 = util$1;


/**
 * 解析style
 *
 * @param {String} css
 * @param {Function} onAttr 处理属性的函数
 *   参数格式： function (sourcePosition, position, name, value, source)
 * @return {String}
 */
function parseStyle$1 (css, onAttr) {
  css = _$3.trimRight(css);
  if (css[css.length - 1] !== ';') css += ';';
  var cssLength = css.length;
  var isParenthesisOpen = false;
  var lastPos = 0;
  var i = 0;
  var retCSS = '';

  function addNewAttr () {
    // 如果没有正常的闭合圆括号，则直接忽略当前属性
    if (!isParenthesisOpen) {
      var source = _$3.trim(css.slice(lastPos, i));
      var j = source.indexOf(':');
      if (j !== -1) {
        var name = _$3.trim(source.slice(0, j));
        var value = _$3.trim(source.slice(j + 1));
        // 必须有属性名称
        if (name) {
          var ret = onAttr(lastPos, retCSS.length, name, value, source);
          if (ret) retCSS += ret + '; ';
        }
      }
    }
    lastPos = i + 1;
  }

  for (; i < cssLength; i++) {
    var c = css[i];
    if (c === '/' && css[i + 1] === '*') {
      // 备注开始
      var j = css.indexOf('*/', i + 2);
      // 如果没有正常的备注结束，则后面的部分全部跳过
      if (j === -1) break;
      // 直接将当前位置调到备注结尾，并且初始化状态
      i = j + 1;
      lastPos = i + 1;
      isParenthesisOpen = false;
    } else if (c === '(') {
      isParenthesisOpen = true;
    } else if (c === ')') {
      isParenthesisOpen = false;
    } else if (c === ';') {
      if (isParenthesisOpen) ; else {
        addNewAttr();
      }
    } else if (c === '\n') {
      addNewAttr();
    }
  }

  return _$3.trim(retCSS);
}

var parser$2 = parseStyle$1;

/**
 * cssfilter
 *
 * @author 老雷<leizongmin@gmail.com>
 */

var DEFAULT$1 = _default;
var parseStyle = parser$2;


/**
 * 返回值是否为空
 *
 * @param {Object} obj
 * @return {Boolean}
 */
function isNull$1 (obj) {
  return (obj === undefined || obj === null);
}

/**
 * 浅拷贝对象
 *
 * @param {Object} obj
 * @return {Object}
 */
function shallowCopyObject$1 (obj) {
  var ret = {};
  for (var i in obj) {
    ret[i] = obj[i];
  }
  return ret;
}

/**
 * 创建CSS过滤器
 *
 * @param {Object} options
 *   - {Object} whiteList
 *   - {Function} onAttr
 *   - {Function} onIgnoreAttr
 *   - {Function} safeAttrValue
 */
function FilterCSS$2 (options) {
  options = shallowCopyObject$1(options || {});
  options.whiteList = options.whiteList || DEFAULT$1.whiteList;
  options.onAttr = options.onAttr || DEFAULT$1.onAttr;
  options.onIgnoreAttr = options.onIgnoreAttr || DEFAULT$1.onIgnoreAttr;
  options.safeAttrValue = options.safeAttrValue || DEFAULT$1.safeAttrValue;
  this.options = options;
}

FilterCSS$2.prototype.process = function (css) {
  // 兼容各种奇葩输入
  css = css || '';
  css = css.toString();
  if (!css) return '';

  var me = this;
  var options = me.options;
  var whiteList = options.whiteList;
  var onAttr = options.onAttr;
  var onIgnoreAttr = options.onIgnoreAttr;
  var safeAttrValue = options.safeAttrValue;

  var retCSS = parseStyle(css, function (sourcePosition, position, name, value, source) {

    var check = whiteList[name];
    var isWhite = false;
    if (check === true) isWhite = check;
    else if (typeof check === 'function') isWhite = check(value);
    else if (check instanceof RegExp) isWhite = check.test(value);
    if (isWhite !== true) isWhite = false;

    // 如果过滤后 value 为空则直接忽略
    value = safeAttrValue(name, value);
    if (!value) return;

    var opts = {
      position: position,
      sourcePosition: sourcePosition,
      source: source,
      isWhite: isWhite
    };

    if (isWhite) {

      var ret = onAttr(name, value, opts);
      if (isNull$1(ret)) {
        return name + ':' + value;
      } else {
        return ret;
      }

    } else {

      var ret = onIgnoreAttr(name, value, opts);
      if (!isNull$1(ret)) {
        return ret;
      }

    }
  });

  return retCSS;
};


var css = FilterCSS$2;

/**
 * cssfilter
 *
 * @author 老雷<leizongmin@gmail.com>
 */

(function (module, exports) {
var DEFAULT = _default;
var FilterCSS = css;


/**
 * XSS过滤
 *
 * @param {String} css 要过滤的CSS代码
 * @param {Object} options 选项：whiteList, onAttr, onIgnoreAttr
 * @return {String}
 */
function filterCSS (html, options) {
  var xss = new FilterCSS(options);
  return xss.process(html);
}


// 输出
exports = module.exports = filterCSS;
exports.FilterCSS = FilterCSS;
for (var i in DEFAULT) exports[i] = DEFAULT[i];

// 在浏览器端使用
if (typeof window !== 'undefined') {
  window.filterCSS = module.exports;
}
}(lib, lib.exports));

var util = {
  indexOf: function (arr, item) {
    var i, j;
    if (Array.prototype.indexOf) {
      return arr.indexOf(item);
    }
    for (i = 0, j = arr.length; i < j; i++) {
      if (arr[i] === item) {
        return i;
      }
    }
    return -1;
  },
  forEach: function (arr, fn, scope) {
    var i, j;
    if (Array.prototype.forEach) {
      return arr.forEach(fn, scope);
    }
    for (i = 0, j = arr.length; i < j; i++) {
      fn.call(scope, arr[i], i, arr);
    }
  },
  trim: function (str) {
    if (String.prototype.trim) {
      return str.trim();
    }
    return str.replace(/(^\s*)|(\s*$)/g, "");
  },
  spaceIndex: function (str) {
    var reg = /\s|\n|\t/;
    var match = reg.exec(str);
    return match ? match.index : -1;
  },
};

/**
 * default settings
 *
 * @author Zongmin Lei<leizongmin@gmail.com>
 */

var FilterCSS$1 = lib.exports.FilterCSS;
var getDefaultCSSWhiteList = lib.exports.getDefaultWhiteList;
var _$2 = util;

function getDefaultWhiteList() {
  return {
    a: ["target", "href", "title"],
    abbr: ["title"],
    address: [],
    area: ["shape", "coords", "href", "alt"],
    article: [],
    aside: [],
    audio: [
      "autoplay",
      "controls",
      "crossorigin",
      "loop",
      "muted",
      "preload",
      "src",
    ],
    b: [],
    bdi: ["dir"],
    bdo: ["dir"],
    big: [],
    blockquote: ["cite"],
    br: [],
    caption: [],
    center: [],
    cite: [],
    code: [],
    col: ["align", "valign", "span", "width"],
    colgroup: ["align", "valign", "span", "width"],
    dd: [],
    del: ["datetime"],
    details: ["open"],
    div: [],
    dl: [],
    dt: [],
    em: [],
    figcaption: [],
    figure: [],
    font: ["color", "size", "face"],
    footer: [],
    h1: [],
    h2: [],
    h3: [],
    h4: [],
    h5: [],
    h6: [],
    header: [],
    hr: [],
    i: [],
    img: ["src", "alt", "title", "width", "height", "loading"],
    ins: ["datetime"],
    kbd: [],
    li: [],
    mark: [],
    nav: [],
    ol: [],
    p: [],
    pre: [],
    s: [],
    section: [],
    small: [],
    span: [],
    sub: [],
    summary: [],
    sup: [],
    strong: [],
    strike: [],
    table: ["width", "border", "align", "valign"],
    tbody: ["align", "valign"],
    td: ["width", "rowspan", "colspan", "align", "valign"],
    tfoot: ["align", "valign"],
    th: ["width", "rowspan", "colspan", "align", "valign"],
    thead: ["align", "valign"],
    tr: ["rowspan", "align", "valign"],
    tt: [],
    u: [],
    ul: [],
    video: [
      "autoplay",
      "controls",
      "crossorigin",
      "loop",
      "muted",
      "playsinline",
      "poster",
      "preload",
      "src",
      "height",
      "width",
    ],
  };
}

var defaultCSSFilter = new FilterCSS$1();

/**
 * default onTag function
 *
 * @param {String} tag
 * @param {String} html
 * @param {Object} options
 * @return {String}
 */
function onTag(tag, html, options) {
  // do nothing
}

/**
 * default onIgnoreTag function
 *
 * @param {String} tag
 * @param {String} html
 * @param {Object} options
 * @return {String}
 */
function onIgnoreTag(tag, html, options) {
  // do nothing
}

/**
 * default onTagAttr function
 *
 * @param {String} tag
 * @param {String} name
 * @param {String} value
 * @return {String}
 */
function onTagAttr(tag, name, value) {
  // do nothing
}

/**
 * default onIgnoreTagAttr function
 *
 * @param {String} tag
 * @param {String} name
 * @param {String} value
 * @return {String}
 */
function onIgnoreTagAttr(tag, name, value) {
  // do nothing
}

/**
 * default escapeHtml function
 *
 * @param {String} html
 */
function escapeHtml(html) {
  return html.replace(REGEXP_LT, "&lt;").replace(REGEXP_GT, "&gt;");
}

/**
 * default safeAttrValue function
 *
 * @param {String} tag
 * @param {String} name
 * @param {String} value
 * @param {Object} cssFilter
 * @return {String}
 */
function safeAttrValue(tag, name, value, cssFilter) {
  // unescape attribute value firstly
  value = friendlyAttrValue(value);

  if (name === "href" || name === "src") {
    // filter `href` and `src` attribute
    // only allow the value that starts with `http://` | `https://` | `mailto:` | `/` | `#`
    value = _$2.trim(value);
    if (value === "#") return "#";
    if (
      !(
        value.substr(0, 7) === "http://" ||
        value.substr(0, 8) === "https://" ||
        value.substr(0, 7) === "mailto:" ||
        value.substr(0, 4) === "tel:" ||
        value.substr(0, 11) === "data:image/" ||
        value.substr(0, 6) === "ftp://" ||
        value.substr(0, 2) === "./" ||
        value.substr(0, 3) === "../" ||
        value[0] === "#" ||
        value[0] === "/"
      )
    ) {
      return "";
    }
  } else if (name === "background") {
    // filter `background` attribute (maybe no use)
    // `javascript:`
    REGEXP_DEFAULT_ON_TAG_ATTR_4.lastIndex = 0;
    if (REGEXP_DEFAULT_ON_TAG_ATTR_4.test(value)) {
      return "";
    }
  } else if (name === "style") {
    // `expression()`
    REGEXP_DEFAULT_ON_TAG_ATTR_7.lastIndex = 0;
    if (REGEXP_DEFAULT_ON_TAG_ATTR_7.test(value)) {
      return "";
    }
    // `url()`
    REGEXP_DEFAULT_ON_TAG_ATTR_8.lastIndex = 0;
    if (REGEXP_DEFAULT_ON_TAG_ATTR_8.test(value)) {
      REGEXP_DEFAULT_ON_TAG_ATTR_4.lastIndex = 0;
      if (REGEXP_DEFAULT_ON_TAG_ATTR_4.test(value)) {
        return "";
      }
    }
    if (cssFilter !== false) {
      cssFilter = cssFilter || defaultCSSFilter;
      value = cssFilter.process(value);
    }
  }

  // escape `<>"` before returns
  value = escapeAttrValue(value);
  return value;
}

// RegExp list
var REGEXP_LT = /</g;
var REGEXP_GT = />/g;
var REGEXP_QUOTE = /"/g;
var REGEXP_QUOTE_2 = /&quot;/g;
var REGEXP_ATTR_VALUE_1 = /&#([a-zA-Z0-9]*);?/gim;
var REGEXP_ATTR_VALUE_COLON = /&colon;?/gim;
var REGEXP_ATTR_VALUE_NEWLINE = /&newline;?/gim;
// var REGEXP_DEFAULT_ON_TAG_ATTR_3 = /\/\*|\*\//gm;
var REGEXP_DEFAULT_ON_TAG_ATTR_4 =
  /((j\s*a\s*v\s*a|v\s*b|l\s*i\s*v\s*e)\s*s\s*c\s*r\s*i\s*p\s*t\s*|m\s*o\s*c\s*h\s*a):/gi;
// var REGEXP_DEFAULT_ON_TAG_ATTR_5 = /^[\s"'`]*(d\s*a\s*t\s*a\s*)\:/gi;
// var REGEXP_DEFAULT_ON_TAG_ATTR_6 = /^[\s"'`]*(d\s*a\s*t\s*a\s*)\:\s*image\//gi;
var REGEXP_DEFAULT_ON_TAG_ATTR_7 =
  /e\s*x\s*p\s*r\s*e\s*s\s*s\s*i\s*o\s*n\s*\(.*/gi;
var REGEXP_DEFAULT_ON_TAG_ATTR_8 = /u\s*r\s*l\s*\(.*/gi;

/**
 * escape double quote
 *
 * @param {String} str
 * @return {String} str
 */
function escapeQuote(str) {
  return str.replace(REGEXP_QUOTE, "&quot;");
}

/**
 * unescape double quote
 *
 * @param {String} str
 * @return {String} str
 */
function unescapeQuote(str) {
  return str.replace(REGEXP_QUOTE_2, '"');
}

/**
 * escape html entities
 *
 * @param {String} str
 * @return {String}
 */
function escapeHtmlEntities(str) {
  return str.replace(REGEXP_ATTR_VALUE_1, function replaceUnicode(str, code) {
    return code[0] === "x" || code[0] === "X"
      ? String.fromCharCode(parseInt(code.substr(1), 16))
      : String.fromCharCode(parseInt(code, 10));
  });
}

/**
 * escape html5 new danger entities
 *
 * @param {String} str
 * @return {String}
 */
function escapeDangerHtml5Entities(str) {
  return str
    .replace(REGEXP_ATTR_VALUE_COLON, ":")
    .replace(REGEXP_ATTR_VALUE_NEWLINE, " ");
}

/**
 * clear nonprintable characters
 *
 * @param {String} str
 * @return {String}
 */
function clearNonPrintableCharacter(str) {
  var str2 = "";
  for (var i = 0, len = str.length; i < len; i++) {
    str2 += str.charCodeAt(i) < 32 ? " " : str.charAt(i);
  }
  return _$2.trim(str2);
}

/**
 * get friendly attribute value
 *
 * @param {String} str
 * @return {String}
 */
function friendlyAttrValue(str) {
  str = unescapeQuote(str);
  str = escapeHtmlEntities(str);
  str = escapeDangerHtml5Entities(str);
  str = clearNonPrintableCharacter(str);
  return str;
}

/**
 * unescape attribute value
 *
 * @param {String} str
 * @return {String}
 */
function escapeAttrValue(str) {
  str = escapeQuote(str);
  str = escapeHtml(str);
  return str;
}

/**
 * `onIgnoreTag` function for removing all the tags that are not in whitelist
 */
function onIgnoreTagStripAll() {
  return "";
}

/**
 * remove tag body
 * specify a `tags` list, if the tag is not in the `tags` list then process by the specify function (optional)
 *
 * @param {array} tags
 * @param {function} next
 */
function StripTagBody(tags, next) {
  if (typeof next !== "function") {
    next = function () {};
  }

  var isRemoveAllTag = !Array.isArray(tags);
  function isRemoveTag(tag) {
    if (isRemoveAllTag) return true;
    return _$2.indexOf(tags, tag) !== -1;
  }

  var removeList = [];
  var posStart = false;

  return {
    onIgnoreTag: function (tag, html, options) {
      if (isRemoveTag(tag)) {
        if (options.isClosing) {
          var ret = "[/removed]";
          var end = options.position + ret.length;
          removeList.push([
            posStart !== false ? posStart : options.position,
            end,
          ]);
          posStart = false;
          return ret;
        } else {
          if (!posStart) {
            posStart = options.position;
          }
          return "[removed]";
        }
      } else {
        return next(tag, html, options);
      }
    },
    remove: function (html) {
      var rethtml = "";
      var lastPos = 0;
      _$2.forEach(removeList, function (pos) {
        rethtml += html.slice(lastPos, pos[0]);
        lastPos = pos[1];
      });
      rethtml += html.slice(lastPos);
      return rethtml;
    },
  };
}

/**
 * remove html comments
 *
 * @param {String} html
 * @return {String}
 */
function stripCommentTag(html) {
  var retHtml = "";
  var lastPos = 0;
  while (lastPos < html.length) {
    var i = html.indexOf("<!--", lastPos);
    if (i === -1) {
      retHtml += html.slice(lastPos);
      break;
    }
    retHtml += html.slice(lastPos, i);
    var j = html.indexOf("-->", i);
    if (j === -1) {
      break;
    }
    lastPos = j + 3;
  }
  return retHtml;
}

/**
 * remove invisible characters
 *
 * @param {String} html
 * @return {String}
 */
function stripBlankChar(html) {
  var chars = html.split("");
  chars = chars.filter(function (char) {
    var c = char.charCodeAt(0);
    if (c === 127) return false;
    if (c <= 31) {
      if (c === 10 || c === 13) return true;
      return false;
    }
    return true;
  });
  return chars.join("");
}

_default$1.whiteList = getDefaultWhiteList();
_default$1.getDefaultWhiteList = getDefaultWhiteList;
_default$1.onTag = onTag;
_default$1.onIgnoreTag = onIgnoreTag;
_default$1.onTagAttr = onTagAttr;
_default$1.onIgnoreTagAttr = onIgnoreTagAttr;
_default$1.safeAttrValue = safeAttrValue;
_default$1.escapeHtml = escapeHtml;
_default$1.escapeQuote = escapeQuote;
_default$1.unescapeQuote = unescapeQuote;
_default$1.escapeHtmlEntities = escapeHtmlEntities;
_default$1.escapeDangerHtml5Entities = escapeDangerHtml5Entities;
_default$1.clearNonPrintableCharacter = clearNonPrintableCharacter;
_default$1.friendlyAttrValue = friendlyAttrValue;
_default$1.escapeAttrValue = escapeAttrValue;
_default$1.onIgnoreTagStripAll = onIgnoreTagStripAll;
_default$1.StripTagBody = StripTagBody;
_default$1.stripCommentTag = stripCommentTag;
_default$1.stripBlankChar = stripBlankChar;
_default$1.attributeWrapSign = '"';
_default$1.cssFilter = defaultCSSFilter;
_default$1.getDefaultCSSWhiteList = getDefaultCSSWhiteList;

var parser$1 = {};

/**
 * Simple HTML Parser
 *
 * @author Zongmin Lei<leizongmin@gmail.com>
 */

var _$1 = util;

/**
 * get tag name
 *
 * @param {String} html e.g. '<a hef="#">'
 * @return {String}
 */
function getTagName(html) {
  var i = _$1.spaceIndex(html);
  var tagName;
  if (i === -1) {
    tagName = html.slice(1, -1);
  } else {
    tagName = html.slice(1, i + 1);
  }
  tagName = _$1.trim(tagName).toLowerCase();
  if (tagName.slice(0, 1) === "/") tagName = tagName.slice(1);
  if (tagName.slice(-1) === "/") tagName = tagName.slice(0, -1);
  return tagName;
}

/**
 * is close tag?
 *
 * @param {String} html 如：'<a hef="#">'
 * @return {Boolean}
 */
function isClosing(html) {
  return html.slice(0, 2) === "</";
}

/**
 * parse input html and returns processed html
 *
 * @param {String} html
 * @param {Function} onTag e.g. function (sourcePosition, position, tag, html, isClosing)
 * @param {Function} escapeHtml
 * @return {String}
 */
function parseTag$1(html, onTag, escapeHtml) {

  var rethtml = "";
  var lastPos = 0;
  var tagStart = false;
  var quoteStart = false;
  var currentPos = 0;
  var len = html.length;
  var currentTagName = "";
  var currentHtml = "";

  chariterator: for (currentPos = 0; currentPos < len; currentPos++) {
    var c = html.charAt(currentPos);
    if (tagStart === false) {
      if (c === "<") {
        tagStart = currentPos;
        continue;
      }
    } else {
      if (quoteStart === false) {
        if (c === "<") {
          rethtml += escapeHtml(html.slice(lastPos, currentPos));
          tagStart = currentPos;
          lastPos = currentPos;
          continue;
        }
        if (c === ">" || currentPos === len - 1) {
          rethtml += escapeHtml(html.slice(lastPos, tagStart));
          currentHtml = html.slice(tagStart, currentPos + 1);
          currentTagName = getTagName(currentHtml);
          rethtml += onTag(
            tagStart,
            rethtml.length,
            currentTagName,
            currentHtml,
            isClosing(currentHtml)
          );
          lastPos = currentPos + 1;
          tagStart = false;
          continue;
        }
        if (c === '"' || c === "'") {
          var i = 1;
          var ic = html.charAt(currentPos - i);

          while (ic.trim() === "" || ic === "=") {
            if (ic === "=") {
              quoteStart = c;
              continue chariterator;
            }
            ic = html.charAt(currentPos - ++i);
          }
        }
      } else {
        if (c === quoteStart) {
          quoteStart = false;
          continue;
        }
      }
    }
  }
  if (lastPos < len) {
    rethtml += escapeHtml(html.substr(lastPos));
  }

  return rethtml;
}

var REGEXP_ILLEGAL_ATTR_NAME = /[^a-zA-Z0-9\\_:.-]/gim;

/**
 * parse input attributes and returns processed attributes
 *
 * @param {String} html e.g. `href="#" target="_blank"`
 * @param {Function} onAttr e.g. `function (name, value)`
 * @return {String}
 */
function parseAttr$1(html, onAttr) {

  var lastPos = 0;
  var lastMarkPos = 0;
  var retAttrs = [];
  var tmpName = false;
  var len = html.length;

  function addAttr(name, value) {
    name = _$1.trim(name);
    name = name.replace(REGEXP_ILLEGAL_ATTR_NAME, "").toLowerCase();
    if (name.length < 1) return;
    var ret = onAttr(name, value || "");
    if (ret) retAttrs.push(ret);
  }

  // 逐个分析字符
  for (var i = 0; i < len; i++) {
    var c = html.charAt(i);
    var v, j;
    if (tmpName === false && c === "=") {
      tmpName = html.slice(lastPos, i);
      lastPos = i + 1;
      lastMarkPos = html.charAt(lastPos) === '"' || html.charAt(lastPos) === "'" ? lastPos : findNextQuotationMark(html, i + 1);
      continue;
    }
    if (tmpName !== false) {
      if (
        i === lastMarkPos
      ) {
        j = html.indexOf(c, i + 1);
        if (j === -1) {
          break;
        } else {
          v = _$1.trim(html.slice(lastMarkPos + 1, j));
          addAttr(tmpName, v);
          tmpName = false;
          i = j;
          lastPos = i + 1;
          continue;
        }
      }
    }
    if (/\s|\n|\t/.test(c)) {
      html = html.replace(/\s|\n|\t/g, " ");
      if (tmpName === false) {
        j = findNextEqual(html, i);
        if (j === -1) {
          v = _$1.trim(html.slice(lastPos, i));
          addAttr(v);
          tmpName = false;
          lastPos = i + 1;
          continue;
        } else {
          i = j - 1;
          continue;
        }
      } else {
        j = findBeforeEqual(html, i - 1);
        if (j === -1) {
          v = _$1.trim(html.slice(lastPos, i));
          v = stripQuoteWrap(v);
          addAttr(tmpName, v);
          tmpName = false;
          lastPos = i + 1;
          continue;
        } else {
          continue;
        }
      }
    }
  }

  if (lastPos < html.length) {
    if (tmpName === false) {
      addAttr(html.slice(lastPos));
    } else {
      addAttr(tmpName, stripQuoteWrap(_$1.trim(html.slice(lastPos))));
    }
  }

  return _$1.trim(retAttrs.join(" "));
}

function findNextEqual(str, i) {
  for (; i < str.length; i++) {
    var c = str[i];
    if (c === " ") continue;
    if (c === "=") return i;
    return -1;
  }
}

function findNextQuotationMark(str, i) {
  for (; i < str.length; i++) {
    var c = str[i];
    if (c === " ") continue;
    if (c === "'" || c === '"') return i;
    return -1;
  }
}

function findBeforeEqual(str, i) {
  for (; i > 0; i--) {
    var c = str[i];
    if (c === " ") continue;
    if (c === "=") return i;
    return -1;
  }
}

function isQuoteWrapString(text) {
  if (
    (text[0] === '"' && text[text.length - 1] === '"') ||
    (text[0] === "'" && text[text.length - 1] === "'")
  ) {
    return true;
  } else {
    return false;
  }
}

function stripQuoteWrap(text) {
  if (isQuoteWrapString(text)) {
    return text.substr(1, text.length - 2);
  } else {
    return text;
  }
}

parser$1.parseTag = parseTag$1;
parser$1.parseAttr = parseAttr$1;

/**
 * filter xss
 *
 * @author Zongmin Lei<leizongmin@gmail.com>
 */

var FilterCSS = lib.exports.FilterCSS;
var DEFAULT = _default$1;
var parser = parser$1;
var parseTag = parser.parseTag;
var parseAttr = parser.parseAttr;
var _ = util;

/**
 * returns `true` if the input value is `undefined` or `null`
 *
 * @param {Object} obj
 * @return {Boolean}
 */
function isNull(obj) {
  return obj === undefined || obj === null;
}

/**
 * get attributes for a tag
 *
 * @param {String} html
 * @return {Object}
 *   - {String} html
 *   - {Boolean} closing
 */
function getAttrs(html) {
  var i = _.spaceIndex(html);
  if (i === -1) {
    return {
      html: "",
      closing: html[html.length - 2] === "/",
    };
  }
  html = _.trim(html.slice(i + 1, -1));
  var isClosing = html[html.length - 1] === "/";
  if (isClosing) html = _.trim(html.slice(0, -1));
  return {
    html: html,
    closing: isClosing,
  };
}

/**
 * shallow copy
 *
 * @param {Object} obj
 * @return {Object}
 */
function shallowCopyObject(obj) {
  var ret = {};
  for (var i in obj) {
    ret[i] = obj[i];
  }
  return ret;
}

function keysToLowerCase(obj) {
  var ret = {};
  for (var i in obj) {
    if (Array.isArray(obj[i])) {
      ret[i.toLowerCase()] = obj[i].map(function (item) {
        return item.toLowerCase();
      });
    } else {
      ret[i.toLowerCase()] = obj[i];
    }
  }
  return ret;
}

/**
 * FilterXSS class
 *
 * @param {Object} options
 *        whiteList (or allowList), onTag, onTagAttr, onIgnoreTag,
 *        onIgnoreTagAttr, safeAttrValue, escapeHtml
 *        stripIgnoreTagBody, allowCommentTag, stripBlankChar
 *        css{whiteList, onAttr, onIgnoreAttr} `css=false` means don't use `cssfilter`
 */
function FilterXSS(options) {
  options = shallowCopyObject(options || {});

  if (options.stripIgnoreTag) {
    if (options.onIgnoreTag) {
      console.error(
        'Notes: cannot use these two options "stripIgnoreTag" and "onIgnoreTag" at the same time'
      );
    }
    options.onIgnoreTag = DEFAULT.onIgnoreTagStripAll;
  }
  if (options.whiteList || options.allowList) {
    options.whiteList = keysToLowerCase(options.whiteList || options.allowList);
  } else {
    options.whiteList = DEFAULT.whiteList;
  }

  this.attributeWrapSign = options.singleQuotedAttributeValue === true ? "'" : DEFAULT.attributeWrapSign;

  options.onTag = options.onTag || DEFAULT.onTag;
  options.onTagAttr = options.onTagAttr || DEFAULT.onTagAttr;
  options.onIgnoreTag = options.onIgnoreTag || DEFAULT.onIgnoreTag;
  options.onIgnoreTagAttr = options.onIgnoreTagAttr || DEFAULT.onIgnoreTagAttr;
  options.safeAttrValue = options.safeAttrValue || DEFAULT.safeAttrValue;
  options.escapeHtml = options.escapeHtml || DEFAULT.escapeHtml;
  this.options = options;

  if (options.css === false) {
    this.cssFilter = false;
  } else {
    options.css = options.css || {};
    this.cssFilter = new FilterCSS(options.css);
  }
}

/**
 * start process and returns result
 *
 * @param {String} html
 * @return {String}
 */
FilterXSS.prototype.process = function (html) {
  // compatible with the input
  html = html || "";
  html = html.toString();
  if (!html) return "";

  var me = this;
  var options = me.options;
  var whiteList = options.whiteList;
  var onTag = options.onTag;
  var onIgnoreTag = options.onIgnoreTag;
  var onTagAttr = options.onTagAttr;
  var onIgnoreTagAttr = options.onIgnoreTagAttr;
  var safeAttrValue = options.safeAttrValue;
  var escapeHtml = options.escapeHtml;
  var attributeWrapSign = me.attributeWrapSign;
  var cssFilter = me.cssFilter;

  // remove invisible characters
  if (options.stripBlankChar) {
    html = DEFAULT.stripBlankChar(html);
  }

  // remove html comments
  if (!options.allowCommentTag) {
    html = DEFAULT.stripCommentTag(html);
  }

  // if enable stripIgnoreTagBody
  var stripIgnoreTagBody = false;
  if (options.stripIgnoreTagBody) {
    stripIgnoreTagBody = DEFAULT.StripTagBody(
      options.stripIgnoreTagBody,
      onIgnoreTag
    );
    onIgnoreTag = stripIgnoreTagBody.onIgnoreTag;
  }

  var retHtml = parseTag(
    html,
    function (sourcePosition, position, tag, html, isClosing) {
      var info = {
        sourcePosition: sourcePosition,
        position: position,
        isClosing: isClosing,
        isWhite: Object.prototype.hasOwnProperty.call(whiteList, tag),
      };

      // call `onTag()`
      var ret = onTag(tag, html, info);
      if (!isNull(ret)) return ret;

      if (info.isWhite) {
        if (info.isClosing) {
          return "</" + tag + ">";
        }

        var attrs = getAttrs(html);
        var whiteAttrList = whiteList[tag];
        var attrsHtml = parseAttr(attrs.html, function (name, value) {
          // call `onTagAttr()`
          var isWhiteAttr = _.indexOf(whiteAttrList, name) !== -1;
          var ret = onTagAttr(tag, name, value, isWhiteAttr);
          if (!isNull(ret)) return ret;

          if (isWhiteAttr) {
            // call `safeAttrValue()`
            value = safeAttrValue(tag, name, value, cssFilter);
            if (value) {
              return name + '=' + attributeWrapSign + value + attributeWrapSign;
            } else {
              return name;
            }
          } else {
            // call `onIgnoreTagAttr()`
            ret = onIgnoreTagAttr(tag, name, value, isWhiteAttr);
            if (!isNull(ret)) return ret;
            return;
          }
        });

        // build new tag html
        html = "<" + tag;
        if (attrsHtml) html += " " + attrsHtml;
        if (attrs.closing) html += " /";
        html += ">";
        return html;
      } else {
        // call `onIgnoreTag()`
        ret = onIgnoreTag(tag, html, info);
        if (!isNull(ret)) return ret;
        return escapeHtml(html);
      }
    },
    escapeHtml
  );

  // if enable stripIgnoreTagBody
  if (stripIgnoreTagBody) {
    retHtml = stripIgnoreTagBody.remove(retHtml);
  }

  return retHtml;
};

var xss$1 = FilterXSS;

/**
 * xss
 *
 * @author Zongmin Lei<leizongmin@gmail.com>
 */

(function (module, exports) {
var DEFAULT = _default$1;
var parser = parser$1;
var FilterXSS = xss$1;

/**
 * filter xss function
 *
 * @param {String} html
 * @param {Object} options { whiteList, onTag, onTagAttr, onIgnoreTag, onIgnoreTagAttr, safeAttrValue, escapeHtml }
 * @return {String}
 */
function filterXSS(html, options) {
  var xss = new FilterXSS(options);
  return xss.process(html);
}

exports = module.exports = filterXSS;
exports.filterXSS = filterXSS;
exports.FilterXSS = FilterXSS;

(function () {
  for (var i in DEFAULT) {
    exports[i] = DEFAULT[i];
  }
  for (var j in parser) {
    exports[j] = parser[j];
  }
})();

// using `xss` on the browser, output `filterXSS` to the globals
if (typeof window !== "undefined") {
  window.filterXSS = module.exports;
}

// using `xss` on the WebWorker, output `filterXSS` to the globals
function isWorkerEnv() {
  return (
    typeof self !== "undefined" &&
    typeof DedicatedWorkerGlobalScope !== "undefined" &&
    self instanceof DedicatedWorkerGlobalScope
  );
}
if (isWorkerEnv()) {
  self.filterXSS = module.exports;
}
}(lib$1, lib$1.exports));

const xss = lib$1.exports;

const cosmosWhitelist = {
	"cosmos-action-menu": [
	"align",
	"anchor",
	"appearance",
	"open",
	"placement",
	"trigger"
],
	"cosmos-chip": [
	"appearance",
	"ariaLabel",
	"shape"
],
	"cosmos-flag": [
	"alt",
	"code",
	"loading"
],
	"cosmos-icon-360-photo": [
	"ariaLabel"
],
	"cosmos-icon-360-video": [
	"ariaLabel"
],
	"cosmos-icon-add": [
	"ariaLabel"
],
	"cosmos-icon-airplay": [
	"ariaLabel"
],
	"cosmos-button": [
	"appearance",
	"ariaExpanded",
	"ariaHaspopup",
	"ariaLabel",
	"busy",
	"buttonTitle",
	"decorative",
	"disabled",
	"hovered",
	"href",
	"iconPlacement",
	"kind",
	"rel",
	"shape",
	"size",
	"target",
	"type",
	"width"
],
	"cosmos-avatar": [
	"alt",
	"appearance",
	"framed",
	"loading",
	"name",
	"size",
	"sizes",
	"src",
	"srcset"
],
	"cosmos-badge": [
	"appearance",
	"href",
	"kind",
	"rel",
	"shape",
	"size",
	"target"
],
	"cosmos-icon-amazon-branded-dark": [
	"ariaLabel"
],
	"cosmos-icon-align-right": [
	"ariaLabel"
],
	"cosmos-icon-amazon-branded-light": [
	"ariaLabel"
],
	"cosmos-icon-amazon-music-branded": [
	"ariaLabel"
],
	"cosmos-icon-amazon-music": [
	"ariaLabel"
],
	"cosmos-icon-amazon": [
	"ariaLabel"
],
	"cosmos-icon-apple-podcasts-branded": [
	"ariaLabel"
],
	"cosmos-icon-apple-podcasts": [
	"ariaLabel"
],
	"cosmos-icon-apple": [
	"ariaLabel"
],
	"cosmos-icon-ar-active": [
	"ariaLabel"
],
	"cosmos-icon-ar-label": [
	"ariaLabel"
],
	"cosmos-icon-ar-mobile": [
	"ariaLabel"
],
	"cosmos-icon-ar": [
	"ariaLabel"
],
	"cosmos-icon-archive": [
	"ariaLabel"
],
	"cosmos-icon-arrow-diagonal-down-left": [
	"ariaLabel"
],
	"cosmos-icon-arrow-diagonal-down-right": [
	"ariaLabel"
],
	"cosmos-icon-arrow-diagonal-up-left": [
	"ariaLabel"
],
	"cosmos-icon-arrow-diagonal-up-right": [
	"ariaLabel"
],
	"cosmos-icon-arrow-down": [
	"ariaLabel"
],
	"cosmos-icon-arrow-left": [
	"ariaLabel"
],
	"cosmos-icon-arrow-up": [
	"ariaLabel"
],
	"cosmos-icon-audio-settings": [
	"ariaLabel"
],
	"cosmos-icon-author": [
	"ariaLabel"
],
	"cosmos-icon-back-15": [
	"ariaLabel"
],
	"cosmos-icon-back-20": [
	"ariaLabel"
],
	"cosmos-icon-back": [
	"ariaLabel"
],
	"cosmos-icon-backward": [
	"ariaLabel"
],
	"cosmos-icon-bandsintown-branded": [
	"ariaLabel"
],
	"cosmos-icon-bandsintown": [
	"ariaLabel"
],
	"cosmos-icon-baseball-cap": [
	"ariaLabel"
],
	"cosmos-icon-back-10": [
	"ariaLabel"
],
	"cosmos-icon-bookmark-filled": [
	"ariaLabel"
],
	"cosmos-icon-arrow-right": [
	"ariaLabel"
],
	"cosmos-icon-bookmark": [
	"ariaLabel"
],
	"cosmos-icon-browse": [
	"ariaLabel"
],
	"cosmos-icon-bulb-filled": [
	"ariaLabel"
],
	"cosmos-icon-bulb": [
	"ariaLabel"
],
	"cosmos-icon-browse-filled": [
	"ariaLabel"
],
	"cosmos-icon-buy-tickets": [
	"ariaLabel"
],
	"cosmos-icon-calendar-filled": [
	"ariaLabel"
],
	"cosmos-icon-calendar": [
	"ariaLabel"
],
	"cosmos-icon-can-inverted": [
	"ariaLabel"
],
	"cosmos-icon-can": [
	"ariaLabel"
],
	"cosmos-icon-cancel": [
	"ariaLabel"
],
	"cosmos-icon-captions-filled": [
	"ariaLabel"
],
	"cosmos-icon-captions": [
	"ariaLabel"
],
	"cosmos-icon-caret-down": [
	"ariaLabel"
],
	"cosmos-icon-caret-left": [
	"ariaLabel"
],
	"cosmos-icon-caret-right": [
	"ariaLabel"
],
	"cosmos-icon-caret-up": [
	"ariaLabel"
],
	"cosmos-icon-chat-add": [
	"ariaLabel"
],
	"cosmos-icon-checkbox-unchecked": [
	"ariaLabel"
],
	"cosmos-icon-checkmark": [
	"ariaLabel"
],
	"cosmos-icon-checkout": [
	"ariaLabel"
],
	"cosmos-icon-chevron-down": [
	"ariaLabel"
],
	"cosmos-icon-chevron-left": [
	"ariaLabel"
],
	"cosmos-icon-chevron-right": [
	"ariaLabel"
],
	"cosmos-icon-chevron-up": [
	"ariaLabel"
],
	"cosmos-icon-chromecast-filled": [
	"ariaLabel"
],
	"cosmos-icon-chromecast": [
	"ariaLabel"
],
	"cosmos-icon-clear": [
	"ariaLabel"
],
	"cosmos-icon-clip": [
	"ariaLabel"
],
	"cosmos-icon-clock": [
	"ariaLabel"
],
	"cosmos-icon-close-small": [
	"ariaLabel"
],
	"cosmos-icon-close": [
	"ariaLabel"
],
	"cosmos-icon-cloud": [
	"ariaLabel"
],
	"cosmos-icon-code-block": [
	"ariaLabel"
],
	"cosmos-icon-collapse-horizontal": [
	"ariaLabel"
],
	"cosmos-icon-checkbox-checked": [
	"ariaLabel"
],
	"cosmos-icon-collapse-vertical": [
	"ariaLabel"
],
	"cosmos-icon-columns": [
	"ariaLabel"
],
	"cosmos-icon-comment": [
	"ariaLabel"
],
	"cosmos-icon-contextual": [
	"ariaLabel"
],
	"cosmos-icon-contract": [
	"ariaLabel"
],
	"cosmos-icon-copy-link": [
	"ariaLabel"
],
	"cosmos-icon-country": [
	"ariaLabel"
],
	"cosmos-icon-couple": [
	"ariaLabel"
],
	"cosmos-icon-crashed": [
	"ariaLabel"
],
	"cosmos-icon-create": [
	"ariaLabel"
],
	"cosmos-icon-crew": [
	"ariaLabel"
],
	"cosmos-icon-crop": [
	"ariaLabel"
],
	"cosmos-icon-currency": [
	"ariaLabel"
],
	"cosmos-icon-currently-watching": [
	"ariaLabel"
],
	"cosmos-icon-cut": [
	"ariaLabel"
],
	"cosmos-icon-deezer-branded": [
	"ariaLabel"
],
	"cosmos-icon-deezer": [
	"ariaLabel"
],
	"cosmos-icon-delete": [
	"ariaLabel"
],
	"cosmos-icon-delivery": [
	"ariaLabel"
],
	"cosmos-icon-desktop": [
	"ariaLabel"
],
	"cosmos-icon-devices": [
	"ariaLabel"
],
	"cosmos-icon-directions": [
	"ariaLabel"
],
	"cosmos-icon-discord-branded": [
	"ariaLabel"
],
	"cosmos-icon-discord": [
	"ariaLabel"
],
	"cosmos-icon-discover": [
	"ariaLabel"
],
	"cosmos-icon-document-filing-2": [
	"ariaLabel"
],
	"cosmos-icon-document-filing": [
	"ariaLabel"
],
	"cosmos-icon-document-filled": [
	"ariaLabel"
],
	"cosmos-icon-download-error": [
	"ariaLabel"
],
	"cosmos-icon-download": [
	"ariaLabel"
],
	"cosmos-icon-downloading": [
	"ariaLabel"
],
	"cosmos-icon-drag-handle-horizontal": [
	"ariaLabel"
],
	"cosmos-icon-drag-handle-vertical": [
	"ariaLabel"
],
	"cosmos-icon-drink": [
	"ariaLabel"
],
	"cosmos-icon-duplicate": [
	"ariaLabel"
],
	"cosmos-icon-edit": [
	"ariaLabel"
],
	"cosmos-icon-email": [
	"ariaLabel"
],
	"cosmos-icon-emoji": [
	"ariaLabel"
],
	"cosmos-icon-enlarge": [
	"ariaLabel"
],
	"cosmos-icon-envelope-filled": [
	"ariaLabel"
],
	"cosmos-icon-epg": [
	"ariaLabel"
],
	"cosmos-icon-episode": [
	"ariaLabel"
],
	"cosmos-icon-error-filled": [
	"ariaLabel"
],
	"cosmos-icon-event-details": [
	"ariaLabel"
],
	"cosmos-icon-event-end": [
	"ariaLabel"
],
	"cosmos-icon-event-series": [
	"ariaLabel"
],
	"cosmos-icon-event-start": [
	"ariaLabel"
],
	"cosmos-icon-event": [
	"ariaLabel"
],
	"cosmos-icon-expand-horizontal": [
	"ariaLabel"
],
	"cosmos-icon-expand-vertical": [
	"ariaLabel"
],
	"cosmos-icon-external-link": [
	"ariaLabel"
],
	"cosmos-icon-facebook-branded": [
	"ariaLabel"
],
	"cosmos-icon-facebook": [
	"ariaLabel"
],
	"cosmos-icon-fast-checkout": [
	"ariaLabel"
],
	"cosmos-icon-fast-forward": [
	"ariaLabel"
],
	"cosmos-icon-fast-rewind": [
	"ariaLabel"
],
	"cosmos-icon-feature-film": [
	"ariaLabel"
],
	"cosmos-icon-feedback-filled": [
	"ariaLabel"
],
	"cosmos-icon-feedback": [
	"ariaLabel"
],
	"cosmos-icon-female": [
	"ariaLabel"
],
	"cosmos-icon-file-download": [
	"ariaLabel"
],
	"cosmos-icon-file-upload": [
	"ariaLabel"
],
	"cosmos-icon-film-camera": [
	"ariaLabel"
],
	"cosmos-icon-films": [
	"ariaLabel"
],
	"cosmos-icon-filter-list": [
	"ariaLabel"
],
	"cosmos-icon-filter-remove": [
	"ariaLabel"
],
	"cosmos-icon-filter": [
	"ariaLabel"
],
	"cosmos-icon-flag": [
	"ariaLabel"
],
	"cosmos-icon-folder-add": [
	"ariaLabel"
],
	"cosmos-icon-folder-download": [
	"ariaLabel"
],
	"cosmos-icon-folder-open": [
	"ariaLabel"
],
	"cosmos-icon-folder-remove": [
	"ariaLabel"
],
	"cosmos-icon-folder-shared": [
	"ariaLabel"
],
	"cosmos-icon-file-remove": [
	"ariaLabel"
],
	"cosmos-icon-folder-upload": [
	"ariaLabel"
],
	"cosmos-icon-format-bold": [
	"ariaLabel"
],
	"cosmos-icon-format-quote": [
	"ariaLabel"
],
	"cosmos-icon-format-list-numbered": [
	"ariaLabel"
],
	"cosmos-icon-folder": [
	"ariaLabel"
],
	"cosmos-icon-format-underline": [
	"ariaLabel"
],
	"cosmos-icon-forward-10": [
	"ariaLabel"
],
	"cosmos-icon-format-size": [
	"ariaLabel"
],
	"cosmos-icon-forward-15": [
	"ariaLabel"
],
	"cosmos-icon-forward-20": [
	"ariaLabel"
],
	"cosmos-icon-forward": [
	"ariaLabel"
],
	"cosmos-icon-fullscreen-exit": [
	"ariaLabel"
],
	"cosmos-icon-funnel-remove": [
	"ariaLabel"
],
	"cosmos-icon-fullscreen": [
	"ariaLabel"
],
	"cosmos-icon-funnel": [
	"ariaLabel"
],
	"cosmos-icon-gallery": [
	"ariaLabel"
],
	"cosmos-icon-game": [
	"ariaLabel"
],
	"cosmos-icon-generic-content": [
	"ariaLabel"
],
	"cosmos-icon-globe-geo": [
	"ariaLabel"
],
	"cosmos-icon-google-branded": [
	"ariaLabel"
],
	"cosmos-icon-google-play": [
	"ariaLabel"
],
	"cosmos-icon-google-podcasts-branded": [
	"ariaLabel"
],
	"cosmos-icon-google-podcasts": [
	"ariaLabel"
],
	"cosmos-icon-google": [
	"ariaLabel"
],
	"cosmos-icon-grid-layout": [
	"ariaLabel"
],
	"cosmos-icon-grid-live": [
	"ariaLabel"
],
	"cosmos-icon-grid": [
	"ariaLabel"
],
	"cosmos-icon-headline": [
	"ariaLabel"
],
	"cosmos-icon-headphones": [
	"ariaLabel"
],
	"cosmos-icon-heart-filled": [
	"ariaLabel"
],
	"cosmos-icon-heart": [
	"ariaLabel"
],
	"cosmos-icon-highlights": [
	"ariaLabel"
],
	"cosmos-icon-history": [
	"ariaLabel"
],
	"cosmos-icon-home": [
	"ariaLabel"
],
	"cosmos-icon-home-filled": [
	"ariaLabel"
],
	"cosmos-icon-hot": [
	"ariaLabel"
],
	"cosmos-icon-hourglass-empty": [
	"ariaLabel"
],
	"cosmos-icon-hourglass-full": [
	"ariaLabel"
],
	"cosmos-icon-google-play-branded": [
	"ariaLabel"
],
	"cosmos-icon-image-in-picture": [
	"ariaLabel"
],
	"cosmos-icon-image": [
	"ariaLabel"
],
	"cosmos-icon-in-progress": [
	"ariaLabel"
],
	"cosmos-icon-info-filled": [
	"ariaLabel"
],
	"cosmos-icon-info-heat-analyser": [
	"ariaLabel"
],
	"cosmos-icon-info-multicolored": [
	"ariaLabel"
],
	"cosmos-icon-info": [
	"ariaLabel"
],
	"cosmos-icon-infographic": [
	"ariaLabel"
],
	"cosmos-icon-image-gallery": [
	"ariaLabel"
],
	"cosmos-icon-instagram-branded": [
	"ariaLabel"
],
	"cosmos-icon-instagram": [
	"ariaLabel"
],
	"cosmos-icon-invoice": [
	"ariaLabel"
],
	"cosmos-icon-italic": [
	"ariaLabel"
],
	"cosmos-icon-keyboard": [
	"ariaLabel"
],
	"cosmos-icon-interview": [
	"ariaLabel"
],
	"cosmos-icon-language": [
	"ariaLabel"
],
	"cosmos-icon-label": [
	"ariaLabel"
],
	"cosmos-icon-laptop": [
	"ariaLabel"
],
	"cosmos-icon-launch": [
	"ariaLabel"
],
	"cosmos-icon-link": [
	"ariaLabel"
],
	"cosmos-icon-linkedin-branded": [
	"ariaLabel"
],
	"cosmos-icon-linkedin": [
	"ariaLabel"
],
	"cosmos-icon-list": [
	"ariaLabel"
],
	"cosmos-icon-live-broadcast": [
	"ariaLabel"
],
	"cosmos-icon-live": [
	"ariaLabel"
],
	"cosmos-icon-loading": [
	"ariaLabel"
],
	"cosmos-icon-location": [
	"ariaLabel"
],
	"cosmos-icon-lock-filled": [
	"ariaLabel"
],
	"cosmos-icon-lock-open": [
	"ariaLabel"
],
	"cosmos-icon-magic-wand": [
	"ariaLabel"
],
	"cosmos-icon-male": [
	"ariaLabel"
],
	"cosmos-icon-map": [
	"ariaLabel"
],
	"cosmos-icon-megaphone": [
	"ariaLabel"
],
	"cosmos-icon-menu-notification": [
	"ariaLabel"
],
	"cosmos-icon-menu-pulsating": [
	"ariaLabel"
],
	"cosmos-icon-messenger-branded": [
	"ariaLabel"
],
	"cosmos-icon-messenger": [
	"ariaLabel"
],
	"cosmos-icon-minus": [
	"ariaLabel"
],
	"cosmos-icon-mode-dark": [
	"ariaLabel"
],
	"cosmos-icon-mode-light": [
	"ariaLabel"
],
	"cosmos-icon-more": [
	"ariaLabel"
],
	"cosmos-icon-mountain": [
	"ariaLabel"
],
	"cosmos-icon-align-center": [
	"ariaLabel"
],
	"cosmos-icon-multi-language": [
	"ariaLabel"
],
	"cosmos-icon-my-location": [
	"ariaLabel"
],
	"cosmos-icon-news": [
	"ariaLabel"
],
	"cosmos-icon-no-connection": [
	"ariaLabel"
],
	"cosmos-icon-menu": [
	"ariaLabel"
],
	"cosmos-icon-pagination-filled": [
	"ariaLabel"
],
	"cosmos-icon-pagination": [
	"ariaLabel"
],
	"cosmos-icon-parcel": [
	"ariaLabel"
],
	"cosmos-icon-partner-profile": [
	"ariaLabel"
],
	"cosmos-icon-paste": [
	"ariaLabel"
],
	"cosmos-icon-pause": [
	"ariaLabel"
],
	"cosmos-icon-payment": [
	"ariaLabel"
],
	"cosmos-icon-pen": [
	"ariaLabel"
],
	"cosmos-icon-person-follow": [
	"ariaLabel"
],
	"cosmos-icon-person-following": [
	"ariaLabel"
],
	"cosmos-icon-no-data": [
	"ariaLabel"
],
	"cosmos-icon-person-profile-filled": [
	"ariaLabel"
],
	"cosmos-icon-photo-camera": [
	"ariaLabel"
],
	"cosmos-icon-phone": [
	"ariaLabel"
],
	"cosmos-icon-picture-in-picture": [
	"ariaLabel"
],
	"cosmos-icon-pie-outline": [
	"ariaLabel"
],
	"cosmos-icon-pinterest-branded": [
	"ariaLabel"
],
	"cosmos-icon-pinterest": [
	"ariaLabel"
],
	"cosmos-icon-play": [
	"ariaLabel"
],
	"cosmos-icon-playlist-personalised": [
	"ariaLabel"
],
	"cosmos-icon-playlist": [
	"ariaLabel"
],
	"cosmos-icon-plus": [
	"ariaLabel"
],
	"cosmos-icon-photo-camera-reverse": [
	"ariaLabel"
],
	"cosmos-icon-podcast": [
	"ariaLabel"
],
	"cosmos-icon-presentation": [
	"ariaLabel"
],
	"cosmos-icon-preview-filled": [
	"ariaLabel"
],
	"cosmos-icon-preview-off-filled": [
	"ariaLabel"
],
	"cosmos-icon-preview-off": [
	"ariaLabel"
],
	"cosmos-icon-preview": [
	"ariaLabel"
],
	"cosmos-icon-printer": [
	"ariaLabel"
],
	"cosmos-icon-product-placement": [
	"ariaLabel"
],
	"cosmos-icon-push-pin": [
	"ariaLabel"
],
	"cosmos-icon-quality": [
	"ariaLabel"
],
	"cosmos-icon-quiz": [
	"ariaLabel"
],
	"cosmos-icon-radio-button": [
	"ariaLabel"
],
	"cosmos-icon-reddit": [
	"ariaLabel"
],
	"cosmos-icon-redeem": [
	"ariaLabel"
],
	"cosmos-icon-reddit-branded": [
	"ariaLabel"
],
	"cosmos-icon-redo": [
	"ariaLabel"
],
	"cosmos-icon-related-content": [
	"ariaLabel"
],
	"cosmos-icon-reload": [
	"ariaLabel"
],
	"cosmos-icon-reminder-false": [
	"ariaLabel"
],
	"cosmos-icon-remove": [
	"ariaLabel"
],
	"cosmos-icon-rendition": [
	"ariaLabel"
],
	"cosmos-icon-reminder-true": [
	"ariaLabel"
],
	"cosmos-icon-replay-30": [
	"ariaLabel"
],
	"cosmos-icon-replay-5": [
	"ariaLabel"
],
	"cosmos-icon-report": [
	"ariaLabel"
],
	"cosmos-icon-results": [
	"ariaLabel"
],
	"cosmos-icon-roku-replay": [
	"ariaLabel"
],
	"cosmos-icon-route": [
	"ariaLabel"
],
	"cosmos-icon-ruler": [
	"ariaLabel"
],
	"cosmos-icon-rules": [
	"ariaLabel"
],
	"cosmos-icon-save-false": [
	"ariaLabel"
],
	"cosmos-icon-save-true": [
	"ariaLabel"
],
	"cosmos-icon-save": [
	"ariaLabel"
],
	"cosmos-icon-screen-rotation": [
	"ariaLabel"
],
	"cosmos-icon-search": [
	"ariaLabel"
],
	"cosmos-icon-replay": [
	"ariaLabel"
],
	"cosmos-icon-season": [
	"ariaLabel"
],
	"cosmos-icon-secure": [
	"ariaLabel"
],
	"cosmos-icon-select-all": [
	"ariaLabel"
],
	"cosmos-icon-settings": [
	"ariaLabel"
],
	"cosmos-icon-send": [
	"ariaLabel"
],
	"cosmos-icon-select": [
	"ariaLabel"
],
	"cosmos-icon-share-tab": [
	"ariaLabel"
],
	"cosmos-icon-share": [
	"ariaLabel"
],
	"cosmos-icon-shop": [
	"ariaLabel"
],
	"cosmos-icon-shopping-bag": [
	"ariaLabel"
],
	"cosmos-icon-show": [
	"ariaLabel"
],
	"cosmos-icon-sidebar-off": [
	"ariaLabel"
],
	"cosmos-icon-sidebar-on": [
	"ariaLabel"
],
	"cosmos-icon-sign-in": [
	"ariaLabel"
],
	"cosmos-icon-sign-up": [
	"ariaLabel"
],
	"cosmos-icon-section": [
	"ariaLabel"
],
	"cosmos-icon-skip-next-triangle": [
	"ariaLabel"
],
	"cosmos-icon-skip-next": [
	"ariaLabel"
],
	"cosmos-icon-skip-previous-triangle": [
	"ariaLabel"
],
	"cosmos-icon-sign-out": [
	"ariaLabel"
],
	"cosmos-icon-skip-previous": [
	"ariaLabel"
],
	"cosmos-icon-snapchat-branded-dark": [
	"ariaLabel"
],
	"cosmos-icon-snapchat-branded-light": [
	"ariaLabel"
],
	"cosmos-icon-sneaker": [
	"ariaLabel"
],
	"cosmos-icon-songkick-branded": [
	"ariaLabel"
],
	"cosmos-icon-songkick": [
	"ariaLabel"
],
	"cosmos-icon-sort-a-z": [
	"ariaLabel"
],
	"cosmos-icon-sort-z-a": [
	"ariaLabel"
],
	"cosmos-icon-sort": [
	"ariaLabel"
],
	"cosmos-icon-soundcloud-branded": [
	"ariaLabel"
],
	"cosmos-icon-snapchat": [
	"ariaLabel"
],
	"cosmos-icon-soundcloud": [
	"ariaLabel"
],
	"cosmos-icon-spotify": [
	"ariaLabel"
],
	"cosmos-icon-star-filled": [
	"ariaLabel"
],
	"cosmos-icon-star": [
	"ariaLabel"
],
	"cosmos-icon-starred-filled": [
	"ariaLabel"
],
	"cosmos-icon-starred": [
	"ariaLabel"
],
	"cosmos-icon-start": [
	"ariaLabel"
],
	"cosmos-icon-stop": [
	"ariaLabel"
],
	"cosmos-icon-strava-branded": [
	"ariaLabel"
],
	"cosmos-icon-strava": [
	"ariaLabel"
],
	"cosmos-icon-spotify-branded": [
	"ariaLabel"
],
	"cosmos-icon-story": [
	"ariaLabel"
],
	"cosmos-icon-submit": [
	"ariaLabel"
],
	"cosmos-icon-subtitles-filled": [
	"ariaLabel"
],
	"cosmos-icon-subtitles": [
	"ariaLabel"
],
	"cosmos-icon-success": [
	"ariaLabel"
],
	"cosmos-icon-table": [
	"ariaLabel"
],
	"cosmos-icon-t-shirt": [
	"ariaLabel"
],
	"cosmos-icon-tablet": [
	"ariaLabel"
],
	"cosmos-icon-tap": [
	"ariaLabel"
],
	"cosmos-icon-team": [
	"ariaLabel"
],
	"cosmos-icon-thumbs-up": [
	"ariaLabel"
],
	"cosmos-icon-thumbs-down": [
	"ariaLabel"
],
	"cosmos-icon-tiktok-branded-dark": [
	"ariaLabel"
],
	"cosmos-icon-tiktok-branded-light": [
	"ariaLabel"
],
	"cosmos-icon-tiktok": [
	"ariaLabel"
],
	"cosmos-icon-trailer": [
	"ariaLabel"
],
	"cosmos-icon-translate-alt": [
	"ariaLabel"
],
	"cosmos-icon-translate": [
	"ariaLabel"
],
	"cosmos-icon-trending": [
	"ariaLabel"
],
	"cosmos-icon-trophy": [
	"ariaLabel"
],
	"cosmos-icon-text-format": [
	"ariaLabel"
],
	"cosmos-icon-tv": [
	"ariaLabel"
],
	"cosmos-icon-twitch-branded": [
	"ariaLabel"
],
	"cosmos-icon-twitch": [
	"ariaLabel"
],
	"cosmos-icon-undo": [
	"ariaLabel"
],
	"cosmos-icon-upload": [
	"ariaLabel"
],
	"cosmos-icon-user-filled": [
	"ariaLabel"
],
	"cosmos-icon-user": [
	"ariaLabel"
],
	"cosmos-icon-verified": [
	"ariaLabel"
],
	"cosmos-icon-video": [
	"ariaLabel"
],
	"cosmos-icon-user-settings": [
	"ariaLabel"
],
	"cosmos-icon-vimeo-branded": [
	"ariaLabel"
],
	"cosmos-icon-vimeo": [
	"ariaLabel"
],
	"cosmos-icon-vk-branded": [
	"ariaLabel"
],
	"cosmos-icon-vk": [
	"ariaLabel"
],
	"cosmos-icon-view-more": [
	"ariaLabel"
],
	"cosmos-icon-volume-down": [
	"ariaLabel"
],
	"cosmos-icon-volume-mid": [
	"ariaLabel"
],
	"cosmos-icon-volume-up": [
	"ariaLabel"
],
	"cosmos-icon-volume-low": [
	"ariaLabel"
],
	"cosmos-icon-volume": [
	"ariaLabel"
],
	"cosmos-icon-vote": [
	"ariaLabel"
],
	"cosmos-icon-vr": [
	"ariaLabel"
],
	"cosmos-icon-warehouse": [
	"ariaLabel"
],
	"cosmos-icon-warning": [
	"ariaLabel"
],
	"cosmos-icon-washout": [
	"ariaLabel"
],
	"cosmos-icon-watch": [
	"ariaLabel"
],
	"cosmos-icon-weather": [
	"ariaLabel"
],
	"cosmos-icon-whatsapp-branded": [
	"ariaLabel"
],
	"cosmos-icon-whatsapp": [
	"ariaLabel"
],
	"cosmos-icon-x-branded-dark": [
	"ariaLabel"
],
	"cosmos-icon-x-branded-light": [
	"ariaLabel"
],
	"cosmos-icon-x": [
	"ariaLabel"
],
	"cosmos-icon-youtube-branded": [
	"ariaLabel"
],
	"cosmos-icon-youtube": [
	"ariaLabel"
],
	"cosmos-icon-zoom-out": [
	"ariaLabel"
],
	"cosmos-icon-zoom-in": [
	"ariaLabel"
],
	"cosmos-icon": [
	"ariaLabel"
],
	"cosmos-image": [
	"appearance",
	"fit",
	"image",
	"imageAlt",
	"imagePreview",
	"loading",
	"observeResize",
	"radius",
	"sizes",
	"srcset"
],
	"cosmos-link": [
	"href",
	"kind",
	"rel",
	"target"
],
	"cosmos-media-preview": [
	"active",
	"appearance",
	"image",
	"imageAlt",
	"imagePreview",
	"loading",
	"observeResize",
	"playVideo",
	"radius",
	"sizes",
	"srcset",
	"video"
],
	"cosmos-mode": [
	"mode"
],
	"cosmos-spinner": [
	"appearance"
],
	"cosmos-text": [
	"appearance",
	"clamp",
	"kind",
	"size",
	"spacing",
	"tag",
	"weight"
],
	"cosmos-textarea": [
	"appearance",
	"autofocus",
	"disabled",
	"helpText",
	"invalid",
	"label",
	"maxlength",
	"minlength",
	"name",
	"optionalLabel",
	"placeholder",
	"readonly",
	"required",
	"requiredLabel",
	"resizable",
	"rows",
	"validationMessage",
	"value"
],
	"cosmos-title": [
	"appearance",
	"clamp",
	"kind",
	"size",
	"spacing",
	"tag",
	"weight"
],
	"cosmos-tooltip": [
	"appearance",
	"content",
	"controlled",
	"follow",
	"offset",
	"placement",
	"spacing",
	"unreachable",
	"visible"
],
	"cosmos-default-item": [
	"appearance",
	"headline",
	"href",
	"metaTextPrimary",
	"metaTextPrimaryClamp",
	"metaTextSecondary",
	"metaTextSecondaryClamp",
	"rel",
	"size",
	"target"
],
	"deprecated-component": [
	"deprecatedPropValue",
	"newProp",
	"obsoleteProp",
	"replacedProp",
	"updatedPropValue"
],
	"cosmos-link-item": [
	"additionalInfo",
	"appearance",
	"headline",
	"href",
	"image",
	"rel",
	"size",
	"target"
],
	"cosmos-product-item": [
	"additionalInfo",
	"appearance",
	"href",
	"image",
	"name",
	"rel",
	"size",
	"target",
	"text"
],
	"cosmos-person-item": [
	"additionalInfo",
	"appearance",
	"discipline",
	"external",
	"href",
	"name",
	"nationality",
	"profileImage",
	"profileImageFramed",
	"rel",
	"size",
	"target"
],
	"cosmos-story-item": [
	"additionalInfo",
	"appearance",
	"headline",
	"href",
	"image",
	"readingTime",
	"rel",
	"size",
	"target"
],
	"cosmos-video-item": [
	"additionalInfo",
	"appearance",
	"duration",
	"headline",
	"href",
	"image",
	"inProgress",
	"inProgressText",
	"live",
	"liveText",
	"rel",
	"size",
	"target"
],
	"cosmos-default-card": [
	"appearance",
	"badge",
	"cover",
	"favoritable",
	"favoriteAriaLabel",
	"favorited",
	"headline",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"logo",
	"logoAlt",
	"logoNarrow",
	"metaText",
	"observeMediaResize",
	"rel",
	"tag",
	"target",
	"text",
	"video"
],
	"cosmos-link-card": [
	"appearance",
	"headline",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"logo",
	"logoAlt",
	"logoNarrow",
	"observeMediaResize",
	"rel",
	"tag",
	"target",
	"text",
	"video"
],
	"cosmos-product-card": [
	"appearance",
	"cta",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"logo",
	"logoAlt",
	"logoNarrow",
	"name",
	"observeMediaResize",
	"rel",
	"target",
	"text"
],
	"cosmos-person-card": [
	"appearance",
	"disciplines",
	"external",
	"favoritable",
	"favoriteAriaLabel",
	"favoriteButtonLabel",
	"favorited",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"name",
	"nationality",
	"observeMediaResize",
	"profileImage",
	"rel",
	"target",
	"video"
],
	"cosmos-story-card": [
	"appearance",
	"badge",
	"favoritable",
	"favoriteAriaLabel",
	"favorited",
	"headline",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"logo",
	"logoAlt",
	"logoNarrow",
	"observeMediaResize",
	"playVideo",
	"readingTime",
	"rel",
	"tag",
	"target",
	"text",
	"video"
],
	"cosmos-video-card": [
	"badge",
	"duration",
	"favoritable",
	"favoriteAriaLabel",
	"favorited",
	"headline",
	"href",
	"image",
	"imageLoading",
	"imagePreview",
	"imageSizes",
	"imageSrcset",
	"layout",
	"live",
	"logo",
	"logoAlt",
	"logoNarrow",
	"observeMediaResize",
	"rel",
	"tag",
	"target",
	"text",
	"video"
],
	"cosmos-action-menu-group": [
	"appearance",
	"label"
],
	"cosmos-action-menu-item": [
	"active",
	"appearance",
	"href",
	"itemId",
	"meta",
	"rel",
	"target"
],
	"cosmos-checkbox": [
	"appearance",
	"autofocus",
	"checked",
	"clamp",
	"disabled",
	"helpText",
	"hitArea",
	"invalid",
	"name",
	"optionalLabel",
	"required",
	"requiredLabel",
	"validationMessage",
	"value"
],
	"cosmos-checkbox-group": [
	"appearance",
	"disabled",
	"helpText",
	"invalid",
	"label",
	"optionalLabel",
	"requiredLabel",
	"validationMessage"
],
	"cosmos-input": [
	"appearance",
	"autocomplete",
	"autofocus",
	"clearable",
	"clearAriaLabel",
	"disabled",
	"helpText",
	"iconPlacement",
	"invalid",
	"label",
	"name",
	"optionalLabel",
	"placeholder",
	"readonly",
	"required",
	"requiredLabel",
	"type",
	"validationMessage",
	"value"
],
	"cosmos-optgroup": [
	"appearance",
	"disabled",
	"label"
],
	"cosmos-option": [
	"appearance",
	"disabled",
	"meta",
	"selected",
	"value"
],
	"cosmos-radio": [
	"appearance",
	"autofocus",
	"checked",
	"clamp",
	"disabled",
	"focusable",
	"hitArea",
	"invalid",
	"name",
	"required",
	"value"
],
	"cosmos-radio-group": [
	"appearance",
	"disabled",
	"helpText",
	"invalid",
	"label",
	"optionalLabel",
	"requiredLabel",
	"validationMessage"
],
	"cosmos-select": [
	"appearance",
	"autofocus",
	"clearable",
	"clearAriaLabel",
	"disabled",
	"helpText",
	"invalid",
	"label",
	"multiple",
	"name",
	"optionalLabel",
	"placeholder",
	"required",
	"requiredLabel",
	"searchable",
	"searchEmptyResultsText",
	"searchPlaceholder",
	"validationMessage",
	"value"
],
	"cosmos-select-value": [
	"multiple"
],
	"cosmos-asset-item-base": [
	"appearance",
	"hasAdditionalInfoPrefix",
	"hasPrimaryPrefix",
	"hasSecondaryPrefix",
	"headline",
	"headlineClamp",
	"href",
	"metaTextAdditional",
	"metaTextPrimary",
	"metaTextPrimaryClamp",
	"metaTextSecondary",
	"metaTextSecondaryClamp",
	"rel",
	"size",
	"target"
],
	"cosmos-icon-mouse": [
	"ariaLabel"
],
	"cosmos-icon-align-left": [
	"ariaLabel"
],
	"cosmos-icon-mute": [
	"ariaLabel"
]
};

const generalWhitelist = {
	
};

const cosmosXSSWhitelist = Object.assign(Object.assign({}, generalWhitelist), cosmosWhitelist);

const cosmosSelectCss = "@charset \"UTF-8\";.cosmos-form-field{display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column}.cosmos-form-field__info-container{display:inline-block;margin-bottom:8px}.cosmos-form-field__info-container:not(.cosmos-form-field__info-container--has-label,.cosmos-form-field__info-container--has-help-text,.cosmos-form-field__info-container--has-required-optional-label){display:none}.cosmos-form-field__label-text,.cosmos-form-field__help-text,.cosmos-form-field__required-optional-label,.cosmos-form-field__validation-message{display:inline}.cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color);--cosmos-text-font-family:var(--cosmos-select-label-font-family);--cosmos-text-font-size:var(--cosmos-select-label-font-size);--cosmos-text-font-style:var(--cosmos-select-label-font-style);--cosmos-text-font-weight:var(--cosmos-select-label-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-label-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-label-line-height);--cosmos-text-text-transform:var(--cosmos-select-label-text-transform)}.cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color);--cosmos-text-font-family:var(--cosmos-select-help-text-font-family);--cosmos-text-font-size:var(--cosmos-select-help-text-font-size);--cosmos-text-font-style:var(--cosmos-select-help-text-font-style);--cosmos-text-font-weight:var(--cosmos-select-help-text-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-help-text-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-help-text-line-height);--cosmos-text-text-transform:var(--cosmos-select-help-text-text-transform)}.cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color);--cosmos-text-font-family:var(--cosmos-select-required-optional-label-font-family);--cosmos-text-font-size:var(--cosmos-select-required-optional-label-font-size);--cosmos-text-font-style:var(--cosmos-select-required-optional-label-font-style);--cosmos-text-font-weight:var(--cosmos-select-required-optional-label-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-required-optional-label-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-required-optional-label-line-height);--cosmos-text-text-transform:var(--cosmos-select-required-optional-label-text-transform);display:inline-block;-webkit-margin-start:4px;margin-inline-start:4px;white-space:nowrap}.cosmos-form-field__validation-message{display:-ms-flexbox;display:flex;gap:4px;margin-top:8px}.cosmos-form-field__validation-message-text{--cosmos-text-font-family:var(--cosmos-select-validation-message-font-family);--cosmos-text-font-size:var(--cosmos-select-validation-message-font-size);--cosmos-text-font-style:var(--cosmos-select-validation-message-font-style);--cosmos-text-font-weight:var(--cosmos-select-validation-message-font-weight);--cosmos-text-letter-spacing:var(--cosmos-select-validation-message-letter-spacing);--cosmos-text-line-height:var(--cosmos-select-validation-message-line-height);--cosmos-text-text-transform:var(--cosmos-select-validation-message-text-transform)}.cosmos-form-field__validation-message-icon{--cosmos-icon-color:var(--cosmos-select-invalid-color, #E06600);--cosmos-icon-size:calc(16rem / var(--cosmos-base-font-size, 16));-ms-flex-negative:0;flex-shrink:0;padding:1.5px 0}.cosmos-form-field--invalid .cosmos-form-field__label-text,.cosmos-form-field--invalid .cosmos-form-field__validation-message{--cosmos-text-color:var(--cosmos-select-label-color)}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-dark .cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color, rgba(0, 15, 30, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__label-text{--cosmos-text-color:var(--cosmos-select-label-color, rgba(255, 255, 255, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__help-text{--cosmos-text-color:var(--cosmos-select-help-text-color, rgba(255, 255, 255, 0.45))}.cosmos-form-field--disabled.cosmos-form-field--appearance-light .cosmos-form-field__required-optional-label{--cosmos-text-color:var(--cosmos-select-required-optional-label-color, rgba(0, 15, 30, 0.45))}:host{--cosmos-font-family-text:Bull Text, Helvetica, sans-serif}@supports (font-variation-settings: normal){:host{--cosmos-font-family-text:Bull VF, Helvetica, sans-serif}}:host(:lang(ja)){--cosmos-font-family-text:Noto Sans JP, Helvetica, sans-serif}:host(:lang(ar)){--cosmos-font-family-text:Noto Kufi Arabic, Helvetica, sans-serif}:host(:lang(ko)){--cosmos-font-family-text:Noto Sans KR, Helvetica, sans-serif}:host(:lang(hi)){--cosmos-font-family-text:Noto Sans, Helvetica, sans-serif}:host(:lang(he)){--cosmos-font-family-text:Noto Sans Hebrew, Helvetica, sans-serif}:host(:lang(ka)){--cosmos-font-family-text:Noto Sans Georgian, Helvetica, sans-serif}:host(:lang(th)){--cosmos-font-family-text:Noto Sans Thai, Helvetica, sans-serif}:host(:lang(zh)){--cosmos-font-family-text:Microsoft YaHei, ヒラギノ角ゴ Pro W3, Hiragino Kaku Gothic Pro, Hiragino Kaku Gothic ProN W3, Osaka, メイリオ, Meiryo, ＭＳ Ｐゴシック, MS PGothic, Arial Unicode MS, Tahoma, Helvetica, sans-serif}:host(:lang(zh-Hant)){--cosmos-font-family-text:Microsoft JhengHei, Geneva CY, ヒラギノ角ゴ Pro W3, Hiragino Kaku Gothic Pro, Hiragino Kaku Gothic ProN W3, Osaka, メイリオ, Meiryo, ＭＳ Ｐゴシック, MS PGothic, Arial Unicode MS, Tahoma, Helvetica, sans-serif}:host{display:block;width:100%}.cosmos-select{position:relative}.cosmos-select__input{--cosmos-text-color:var(--cosmos-select-color);--cosmos-text-font-family:var(--cosmos-select-font-family);--cosmos-text-font-size:var(--cosmos-select-font-size);--cosmos-text-font-style:var(--cosmos-select-font-style);--cosmos-text-font-weight:var(--cosmos-select-font-weight);--cosmos-text-line-height:var(--cosmos-select-line-height, calc(38rem / var(--cosmos-base-font-size, 16)));-ms-flex-align:center;align-items:center;border:solid var(--cosmos-select-border-width, 1px);border-radius:var(--cosmos-select-border-radius, 4px);-webkit-box-sizing:border-box;box-sizing:border-box;display:-ms-flexbox;display:flex;gap:8px;-ms-flex-pack:justify;justify-content:space-between;letter-spacing:var(--cosmos-select-letter-spacing, normal);max-width:100%;padding:var(--cosmos-select-padding, 0 12px);position:relative;text-transform:var(--cosmos-select-text-transform, none);-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;width:100%}.cosmos-select__input--invalid:not(.cosmos-select__input--disabled){border-color:var(--cosmos-select-invalid-color, #E06600);-webkit-box-shadow:inset 0 0 0 1px var(--cosmos-select-invalid-color, #E06600);box-shadow:inset 0 0 0 1px var(--cosmos-select-invalid-color, #E06600)}.cosmos-select__input--appearance-dark{background-color:var(--cosmos-select-background-color, #FFFFFF);border-color:var(--cosmos-select-border-color, rgba(0, 15, 30, 0.45));color:var(--cosmos-select-color, #000F1E)}.cosmos-select__input--appearance-dark.cosmos-select__input--placeholder{--cosmos-text-color:var(--cosmos-select-placeholder-color)}.cosmos-select__input--appearance-dark.cosmos-select__input--disabled{background:rgba(0, 15, 30, 0.05);border-color:transparent;color:var(--cosmos-select-color, rgba(0, 15, 30, 0.45))}.cosmos-select__input--appearance-dark.cosmos-select__input--disabled.cosmos-select__input--placeholder{--cosmos-text-color:custom-select-property(\"color\", $cosmos-color-surface-glass-dark-45)}.cosmos-select__input--appearance-dark:not(.cosmos-select__input--disabled) .cosmos-select__icon{color:var(--cosmos-select-arrow-color, #000F1E)}.cosmos-select__input--appearance-dark:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid){background-color:var(--cosmos-select-background-color-hover, #FFFFFF);border-color:var(--cosmos-select-border-color-hover, rgba(0, 15, 30, 0.6))}.cosmos-select__input--appearance-dark:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid):not(.cosmos-select__input--placeholder){color:var(--cosmos-select-color-hover, #000F1E)}.cosmos-select__input--appearance-light{background-color:var(--cosmos-select-background-color, rgba(255, 255, 255, 0.05));border-color:var(--cosmos-select-border-color, rgba(255, 255, 255, 0.45));color:var(--cosmos-select-color, #FFFFFF)}.cosmos-select__input--appearance-light.cosmos-select__input--placeholder{--cosmos-text-color:var(--cosmos-select-placeholder-color)}.cosmos-select__input--appearance-light.cosmos-select__input--disabled{background:rgba(255, 255, 255, 0.05);border-color:transparent;color:var(--cosmos-select-color, rgba(255, 255, 255, 0.45))}.cosmos-select__input--appearance-light.cosmos-select__input--disabled.cosmos-select__input--placeholder{--cosmos-text-color:custom-select-property(\"color\", $cosmos-color-surface-glass-light-45)}.cosmos-select__input--appearance-light:not(.cosmos-select__input--disabled) .cosmos-select__icon{color:var(--cosmos-select-arrow-color, #FFFFFF)}.cosmos-select__input--appearance-light:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid){background-color:var(--cosmos-select-background-color-hover, rgba(255, 255, 255, 0.05));border-color:var(--cosmos-select-border-color-hover, rgba(255, 255, 255, 0.6))}.cosmos-select__input--appearance-light:hover:not(.cosmos-select__input--disabled):not(.cosmos-select__input--invalid):not(.cosmos-select__input--placeholder){color:var(--cosmos-select-color-hover, #FFFFFF)}.cosmos-select__icon{color:var(--cosmos-select-arrow-color, currentColor);-ms-flex:0 0 auto;flex:0 0 auto}.cosmos-select__combobox{-ms-flex-align:baseline;align-items:baseline;display:-ms-flexbox;display:flex;-ms-flex:1 1 auto;flex:1 1 auto;height:calc(38rem / var(--cosmos-base-font-size, 16));overflow:hidden}.cosmos-select__combobox:focus,.cosmos-select__combobox:focus:not(:focus-visible){outline:none}.cosmos-select__combobox~.cosmos-select__focus-ring{display:none;pointer-events:none}.cosmos-select__combobox:focus~.cosmos-select__focus-ring{border-radius:calc(var(--cosmos-select-border-radius, 4px) * 1.5);bottom:calc((4px + 0px) * -1);-webkit-box-shadow:inset 0 0 0 2px #1B6AEE, 0 0 0 2px #FFFFFF;box-shadow:inset 0 0 0 2px #1B6AEE, 0 0 0 2px #FFFFFF;left:calc((4px + 0px) * -1);position:absolute;right:calc((4px + 0px) * -1);top:calc((4px + 0px) * -1);display:block}.cosmos-select__listbox-container{background-color:var(--cosmos-select-options-background-color, #FFFFFF);border-radius:var(--cosmos-select-options-border-radius, 4px);-webkit-box-shadow:0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431);box-shadow:0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431);-webkit-box-sizing:border-box;box-sizing:border-box;overflow:hidden;pointer-events:none;position:absolute;right:0;top:100%;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;visibility:hidden;width:100%;z-index:1}.cosmos-select__listbox-container ::slotted(cosmos-optgroup:last-of-type){--cosmos-optgroup-padding-bottom:4px}.cosmos-select__listbox-container--appearance-light{background-color:#00162B;border:1px solid rgba(255, 255, 255, 0.45)}.cosmos-select__listbox-container--appearance-light ::slotted(cosmos-optgroup:nth-of-type(n+2)){border-top:1px solid rgba(255, 255, 255, 0.2);padding-top:4px}.cosmos-select__listbox-container--appearance-dark{border:1px solid rgba(0, 15, 30, 0.2)}.cosmos-select__listbox-container--appearance-dark ::slotted(cosmos-optgroup:nth-of-type(n+2)){border-top:1px solid rgba(0, 15, 30, 0.1);padding-top:4px}.cosmos-select__listbox-container--open{pointer-events:auto;visibility:visible}.cosmos-select__listbox-container--filtered{--cosmos-optgroup-display:none}.cosmos-select__listbox{display:-ms-flexbox;display:flex;-ms-flex-direction:column;flex-direction:column;left:0;max-height:var(--cosmos-select-options-max-height, 320px);overflow:auto;padding:var(--cosmos-select-options-padding, 4px)}.cosmos-select__listbox:not(:has(.cosmos-select__no-filter-results)){gap:2px}.cosmos-select__clearable-button{--cosmos-button-icon-size:calc(10rem / var(--cosmos-base-font-size, 16))}.cosmos-select__clearable-button::part(base){height:calc(18rem / var(--cosmos-base-font-size, 16));min-width:calc(18rem / var(--cosmos-base-font-size, 16));width:calc(18rem / var(--cosmos-base-font-size, 16))}.cosmos-select__sr-value{border-width:0;clip:rect(0, 0, 0, 0);height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;white-space:nowrap;width:1px}.cosmos-select ::slotted([slot=value]){-ms-flex:1 0 auto;flex:1 0 auto;min-width:0;width:100%}.cosmos-select__overflow-badge{margin-inline:4px}.cosmos-select__search-container{--cosmos-icon-color:var(--cosmos-select-search-color);--cosmos-input-background-color:transparent;--cosmos-input-background-color-hover:transparent;--cosmos-input-border-color:transparent;--cosmos-input-border-color-hover:transparent;--cosmos-input-color:var(--cosmos-select-search-color);--cosmos-input-font-family:var(--cosmos-select-search-font-family);--cosmos-input-font-size:var(--cosmos-select-search-font-size);--cosmos-input-font-style:var(--cosmos-select-search-font-style);--cosmos-input-font-weight:var(--cosmos-select-search-font-weight);--cosmos-input-letter-spacing:var(--cosmos-select-search-letter-spacing);--cosmos-input-placeholder-color:var(--cosmos-select-search-placeholder-color);--cosmos-input-text-transform:var(--cosmos-select-search-text-transform);background-color:var(--cosmos-select-search-background-color);padding:4px}.cosmos-form-field--appearance-light .cosmos-select__search-container{-webkit-box-shadow:inset 0 -1px 0 rgba(255, 255, 255, 0.2);box-shadow:inset 0 -1px 0 rgba(255, 255, 255, 0.2)}.cosmos-form-field--appearance-dark .cosmos-select__search-container{-webkit-box-shadow:inset 0 -1px 0 rgba(0, 15, 30, 0.1);box-shadow:inset 0 -1px 0 rgba(0, 15, 30, 0.1)}.cosmos-select__search-container:hover{--cosmos-input-color-hover:var(--cosmos-select-search-color-hover, var(--cosmos-select-search-color));--cosmos-icon-color:var(--cosmos-select-search-color-hover, var(--cosmos-select-search-color));background-color:var(--cosmos-select-search-background-color-hover, var(--cosmos-select-search-background-color))}.cosmos-form-field--appearance-light .cosmos-select__search-container:hover{-webkit-box-shadow:inset 0 -1px 0 rgba(255, 255, 255, 0.45);box-shadow:inset 0 -1px 0 rgba(255, 255, 255, 0.45)}.cosmos-form-field--appearance-dark .cosmos-select__search-container:hover{-webkit-box-shadow:inset 0 -1px 0 rgba(0, 15, 30, 0.2);box-shadow:inset 0 -1px 0 rgba(0, 15, 30, 0.2)}.cosmos-select__search{--focus-offset:-2px}.cosmos-select__no-filter-results{padding:10px 12px}";
const CosmosSelectStyle0 = cosmosSelectCss;

const generateClassNames = bem('cosmos-select');
const CosmosSelect$1 = /*@__PURE__*/ proxyCustomElement(class CosmosSelect extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.inputchangeEvent = createEvent(this, "inputchange", 6);
        this.inputinvalidEvent = createEvent(this, "inputinvalid", 3);
        // Creates dependency between the select and the select-value component.
        this.dependency = [h("cosmos-select-value", null)];
        this.options = [];
        this.PAGE_SIZE = 10;
        this.selectActions = {
            CLOSE: 'CLOSE',
            SELECT_OPTION_AND_CLOSE: 'SELECT_OPTION_AND_CLOSE',
            FIRST: 'FIRST',
            LAST: 'LAST',
            ACTIVATE_NEXT_OPTION: 'ACTIVATE_NEXT_OPTION',
            OPEN_AND_FOCUS_SEARCH: 'OPEN_AND_FOCUS_SEARCH',
            OPEN_AND_ACTIVATE_OPTION: 'OPEN_AND_ACTIVATE_OPTION',
            INCREMENT_ACTIVE_OPTION: 'INCREMENT_ACTIVE_OPTION',
            DECREMENT_ACTIVE_OPTION: 'DECREMENT_ACTIVE_OPTION',
            ACTIVATE_PREV_OPTION: 'ACTIVATE_PREV_OPTION',
            SELECT_OPTION: 'SELECT_OPTION',
            ROVER_TYPE: 'ROVER_TYPE',
            FOCUS_SEARCH: 'FOCUS_SEARCH',
        };
        this.keystrokeFilterTerm = '';
        this.isNavigatedWithKeyboard = false;
        this.wasSubmitted = false;
        this.selectValueRef = null;
        this.isUpdatingOptions = false;
        this.xssOptions = {
            whiteList: Object.assign(Object.assign({}, cosmosXSSWhitelist), lib$1.exports.whiteList),
            onTagAttr: (tag, name, value) => {
                if (['slot', 'class', 'id'].includes(name)) {
                    return `${name}="${value}"`;
                }
            },
        };
        this.fuseSearchOptions = {
            keys: ['label'],
            includeScore: true,
            threshold: 0.4,
            ignoreLocation: true,
        };
        this.filteredOptions = [];
        this.handleFormData = (event) => {
            this.selectedOptions.forEach((option) => {
                if (this.name && !this.disabled) {
                    event.formData.append(this.name, option.ref.value);
                }
            });
        };
        this.handleFormSubmit = (event) => {
            this.wasSubmitted = true;
            if (this.reportValidity()) {
                event.preventDefault();
                this.reannounceValidationMessage();
            }
        };
        this.handleDocumentClick = () => {
            if (this.isListBoxVisible) {
                // Close the listbox without resetting focus
                this.closeListBox(false);
            }
        };
        this.handleListBoxClick = (event) => {
            // make sure the click on the list is not recognized as a click "outside"
            event.preventDefault();
            event.stopImmediatePropagation();
        };
        this.handleSelectClick = async (event) => {
            var _a, _b;
            // make sure the click on the select is not recognized as a click "outside"
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.disabled) {
                return;
            }
            if (this.isListBoxVisible) {
                this.closeListBox();
            }
            else {
                this.openListBox();
                if (!this.searchable) {
                    let optionToActivate = (_a = this.options[0]) === null || _a === void 0 ? void 0 : _a.ref;
                    if (this.selectedOptions.length > 0) {
                        const firstSelectedOption = this.selectedOptions.reduce((prev, curr) => prev.index < curr.index ? prev : curr);
                        optionToActivate = firstSelectedOption.ref;
                    }
                    if (optionToActivate && !optionToActivate.disabled) {
                        this.activateOption(optionToActivate);
                        await forceFocus(optionToActivate);
                        await nextTick();
                    }
                }
                else {
                    await nextTick();
                    (_b = this.searchInputElement) === null || _b === void 0 ? void 0 : _b.inputFocus();
                }
            }
        };
        this.handleSelectChange = (value) => {
            if (!this.isInvalidStateControlled() && this.wasSubmitted) {
                this.reportValidity();
            }
            this.inputchangeEvent.emit({ value });
        };
        this.handleClearableClick = async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await this.clearSelect();
        };
        this.handleClearableKeyDown = async (event) => {
            if (![' ', 'Enter'].includes(event.key)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            await this.clearSelect();
        };
        this.handleSelectKeyDown = async (event) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            this.isNavigatedWithKeyboard = true;
            if (this.disabled) {
                return;
            }
            const action = this.getActionFromCode(event.code, event.key, event.ctrlKey, event.altKey, event.metaKey);
            if (!action) {
                return;
            }
            if (event.key !== 'Tab') {
                event.preventDefault();
            }
            const visibleOptions = this.filteredOptions;
            const firstVisibleOption = visibleOptions[0];
            const lastVisibleOption = visibleOptions[visibleOptions.length - 1];
            switch (action) {
                case this.selectActions.CLOSE:
                    await this.closeListBox();
                    break;
                case this.selectActions.SELECT_OPTION_AND_CLOSE:
                    if (this.isActiveOptionIndexSet()) {
                        this.selectOption(this.options[this.activeOptionIndex]);
                    }
                    await this.closeListBox();
                    break;
                case this.selectActions.SELECT_OPTION:
                    if (this.isActiveOptionIndexSet()) {
                        this.selectOption(this.options[this.activeOptionIndex]);
                    }
                    break;
                case this.selectActions.OPEN_AND_FOCUS_SEARCH:
                    this.openListBox();
                    await nextTick();
                    (_a = this.searchInputElement) === null || _a === void 0 ? void 0 : _a.inputFocus();
                    break;
                case this.selectActions.OPEN_AND_ACTIVATE_OPTION:
                    this.openListBox();
                    await nextTick();
                    this.activateOption(((_b = this.selectedOptions[0]) === null || _b === void 0 ? void 0 : _b.ref) || ((_c = this.options[0]) === null || _c === void 0 ? void 0 : _c.ref));
                    break;
                case this.selectActions.DECREMENT_ACTIVE_OPTION:
                    /* eslint-disable-next-line no-case-declarations */
                    const prevIncrementIndex = this.isActiveOptionIndexSet()
                        ? Math.max(this.activeOptionIndex - this.PAGE_SIZE, 0)
                        : visibleOptions.length - 1;
                    this.activateOption((_d = visibleOptions[prevIncrementIndex]) === null || _d === void 0 ? void 0 : _d.ref);
                    break;
                case this.selectActions.INCREMENT_ACTIVE_OPTION:
                    /* eslint-disable-next-line no-case-declarations */
                    const nextIncrementIndex = this.isActiveOptionIndexSet()
                        ? Math.min(this.activeOptionIndex + this.PAGE_SIZE, visibleOptions.length - 1)
                        : 0;
                    this.activateOption((_e = visibleOptions[nextIncrementIndex]) === null || _e === void 0 ? void 0 : _e.ref);
                    break;
                case this.selectActions.FIRST:
                    this.openListBox();
                    await nextTick();
                    this.activateOption(firstVisibleOption === null || firstVisibleOption === void 0 ? void 0 : firstVisibleOption.ref);
                    break;
                case this.selectActions.LAST:
                    this.openListBox();
                    await nextTick();
                    this.activateOption(lastVisibleOption === null || lastVisibleOption === void 0 ? void 0 : lastVisibleOption.ref);
                    break;
                case this.selectActions.ACTIVATE_NEXT_OPTION:
                    /* eslint-disable-next-line no-case-declarations */
                    const nextIndex = this.isActiveOptionIndexSet()
                        ? Math.min(this.activeOptionIndex + 1, visibleOptions.length - 1)
                        : 0;
                    this.activateOption((_f = visibleOptions[nextIndex]) === null || _f === void 0 ? void 0 : _f.ref);
                    break;
                case this.selectActions.ACTIVATE_PREV_OPTION:
                    /* eslint-disable-next-line no-case-declarations */
                    const prevIndex = this.isActiveOptionIndexSet()
                        ? Math.max(this.activeOptionIndex - 1, 0)
                        : visibleOptions.length - 1;
                    this.activateOption((_g = visibleOptions[prevIndex]) === null || _g === void 0 ? void 0 : _g.ref);
                    break;
                case this.selectActions.ROVER_TYPE:
                    this.openListBox();
                    await nextTick();
                    this.activateMatchingOption(this.getKeystrokeFilterTerm(event.key));
                    break;
                case this.selectActions.FOCUS_SEARCH:
                    return (_h = this.searchInputElement) === null || _h === void 0 ? void 0 : _h.inputFocus();
            }
        };
        this.getValidationMessage = () => {
            if (this.validationMessage) {
                return this.validationMessage;
            }
            else {
                if (!isNodeEnvironment() && this.form) {
                    const ghostSelect = global$1.document.createElement('select');
                    ghostSelect.name = this.name;
                    ghostSelect.required = !!this.required;
                    ghostSelect.style.display = 'none';
                    this.form.appendChild(ghostSelect);
                    const message = ghostSelect.validationMessage;
                    ghostSelect.remove();
                    return message;
                }
            }
        };
        this.reannounceValidationMessage = async () => {
            if (this.validationMessageRef) {
                // Reset validation message to trigger re-announcement of the live region by screen readers
                // The aria-label is updated instead of the text content to avoid a visual flash
                this.validationMessageRef.ariaLabel = '';
                await nextTick();
                this.validationMessageRef.ariaLabel = this.getValidationMessage();
            }
        };
        this.getPreSelectedOption = () => {
            return this.options.find((option) => option.ref.value === this.value || option.ref.selected);
        };
        this.getPreSelectedOptions = () => {
            return this.options.filter((option) => Array.isArray(this.value) ? this.value.includes(option === null || option === void 0 ? void 0 : option.ref.value) : option === null || option === void 0 ? void 0 : option.ref.selected);
        };
        this.clearSelectValueRef = () => {
            if (this.selectValueRef) {
                this.selectValueRef.remove();
                this.selectValueRef = null;
            }
        };
        this.updateSelectValueSlot = () => {
            this.clearSelectValueRef();
            if (!this.multiple && this.selectedOptions.length === 1) {
                this.selectValueRef = global$1.document.createElement(transformTagName('cosmos-select-value'));
                this.selectValueRef.setAttribute('slot', 'value');
                this.selectValueRef.innerHTML = xss(this.selectedOptions[0].ref.innerHTML, this.xssOptions);
                if (this.selectedOptions[0].ref.meta) {
                    this.selectValueRef.setAttribute('meta', this.selectedOptions[0].ref.meta);
                }
            }
            if (this.multiple && this.selectedOptions.length > 0) {
                // this is a wrapping cosmos-text around all the separate cosmos-select-values
                this.selectValueRef = global$1.document.createElement(transformTagName('cosmos-text'));
                this.selectValueRef.classList.add('cosmos-select-value__wrapping-text');
                this.selectValueRef.setAttribute('slot', 'value');
                this.selectValueRef.setAttribute('size', 'small');
                this.selectValueRef.setAttribute('clamp', '1');
                this.selectedOptions.forEach((option, index) => {
                    var _a, _b;
                    const selectValue = global$1.document.createElement(transformTagName('cosmos-select-value'));
                    selectValue.setAttribute('multiple', 'true');
                    selectValue.innerHTML = xss(option.ref.innerHTML, this.xssOptions);
                    (_a = this.selectValueRef) === null || _a === void 0 ? void 0 : _a.appendChild(selectValue);
                    if (index < this.selectedOptions.length - 1) {
                        (_b = this.selectValueRef) === null || _b === void 0 ? void 0 : _b.appendChild(global$1.document.createTextNode(', '));
                    }
                });
            }
            if (this.element && this.selectValueRef) {
                if (this.element.firstChild) {
                    this.element.insertBefore(this.selectValueRef, this.element.firstChild);
                }
                else {
                    this.element.appendChild(this.selectValueRef);
                }
            }
        };
        this.handleSearchInput = (event) => {
            this.searchTerm = event.detail.value;
        };
        // This is an SSR workaround to ensure the select options appear in the correct appearance
        // This is necessary because the select options are not reactive to changes in the select appearance during re-hydration
        this.handleSlotChange = () => {
            var _a, _b;
            if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a.length) && !((_b = this.optgroups) === null || _b === void 0 ? void 0 : _b.length)) {
                return;
            }
            // Check if any option's appearance doesn't match the computed appearance
            const isAppearanceMismatch = this.options.some((option) => option.ref.appearance !== this.computedAppearance);
            if (isAppearanceMismatch) {
                this.handleAppearanceChange(this.computedAppearance);
            }
        };
        // This is an SSR workaround to ensure the select-value is always in sync with the selected options
        // The select-value slot would always get removed during re-hydration
        this.handleValueSlotChange = () => {
            if (!hasNamedSlot(this.element, 'value')) {
                this.updateSelectValueSlot();
            }
        };
        this.appearance = undefined;
        this.autofocus = undefined;
        this.clearable = undefined;
        this.clearAriaLabel = undefined;
        this.disabled = undefined;
        this.helpText = undefined;
        this.invalid = false;
        this.label = undefined;
        this.multiple = false;
        this.name = undefined;
        this.optionalLabel = undefined;
        this.placeholder = undefined;
        this.required = false;
        this.requiredLabel = undefined;
        this.searchable = false;
        this.searchEmptyResultsText = 'No results found';
        this.searchPlaceholder = 'Search';
        this.validationMessage = undefined;
        this.value = undefined;
        this.activeOptionElement = undefined;
        this.activeOptionIndex = undefined;
        this.computedAppearance = 'dark';
        this.inputInvalid = false;
        this.isListBoxVisible = false;
        this.multipleOverflowCount = 0;
        this.searchTerm = '';
        this.selectedOptions = [];
    }
    handleInvalidChange(newValue) {
        this.inputInvalid = newValue;
    }
    async handleValueChange(newValue) {
        // make sure the value is updated after options are re-initialized if both are updated at the same time
        await nextTick();
        const option = this.options.find((option) => option.ref.value === newValue);
        this.selectOption(option, false);
    }
    handleAppearanceChange(appearance) {
        var _a;
        this.computedAppearance = appearance;
        this.options.forEach((el) => (el.ref.appearance = this.computedAppearance));
        this.optgroups.forEach((el) => (el.appearance = this.computedAppearance));
        (_a = this.selectValueRef) === null || _a === void 0 ? void 0 : _a.setAttribute('appearance', this.computedAppearance);
    }
    handleSelectedOptionsChange() {
        this.updateSelectValueSlot();
    }
    handleSearchTermChange() {
        this.filterOptions(this.searchTerm);
    }
    /**
     * Removes focus from the Input.
     */
    async inputBlur() {
        this.comboBoxElement && this.comboBoxElement.blur();
    }
    /**
     * Sets focus on the Input. This will scroll the element into the view and focus it.
     */
    async inputFocus() {
        this.comboBoxElement && this.comboBoxElement.focus();
    }
    /**
     * Returns the validity status of the Input.
     * Fires an `inputinvalid` event in the case of invalidity.
     */
    async inputCheckValidity() {
        return this.checkValidity();
    }
    /**
     * Performs the same validity checking steps as the inputCheckValidity methods.
     * Fires an `inputinvalid` event and reports the problem to the user in case of invalidity.
     */
    async inputReportValidity() {
        return this.reportValidity();
    }
    handleOptionSelect(event) {
        const option = this.options.find((option) => option.ref.value === event.detail.value);
        this.activeOptionIndex = option === null || option === void 0 ? void 0 : option.index;
        this.selectOption(option);
        if (!this.multiple) {
            this.closeListBox();
        }
    }
    handleKeyDown(event) {
        this.handleSelectKeyDown(event);
    }
    async closeListBox(resetFocus = true) {
        var _a;
        if (!this.isListBoxVisible)
            return;
        this.isListBoxVisible = false;
        this.isNavigatedWithKeyboard = false;
        this.resetActiveOption();
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.hide();
        global$1.window.removeEventListener('click', this.handleDocumentClick);
        this.searchTerm = '';
        if (resetFocus) {
            await forceFocus(this.comboBoxElement);
        }
    }
    resetActiveOption() {
        this.activeOptionIndex = undefined;
        this.activeOptionElement = undefined;
    }
    openListBox() {
        var _a;
        if (this.isListBoxVisible)
            return;
        this.isListBoxVisible = true;
        (_a = this.tippyInstance) === null || _a === void 0 ? void 0 : _a.show();
        global$1.window.addEventListener('click', this.handleDocumentClick);
    }
    checkValidity() {
        return !(this.required && !this.selectedOptions.length);
    }
    reportValidity() {
        this.inputInvalid = !this.checkValidity();
        if (this.inputInvalid) {
            this.inputinvalidEvent.emit();
        }
        return this.inputInvalid;
    }
    initAutofocus() {
        const isFirstAutofocusElementonPage = global$1.document.querySelector('[autofocus]') === this.element;
        if (isFirstAutofocusElementonPage) {
            setTimeout(async () => {
                await this.inputFocus();
            }, 1);
        }
    }
    isInvalidStateControlled() {
        return this.invalid;
    }
    selectOption(option, shouldEmitEvent = true) {
        var _a, _b;
        if (!option || option.ref.disabled || this.isUpdatingOptions) {
            return;
        }
        const scrollTop = (_b = (_a = this.listBoxElement) === null || _a === void 0 ? void 0 : _a.scrollTop) !== null && _b !== void 0 ? _b : 0;
        if (this.multiple) {
            this.updateMultipleSelection(option, shouldEmitEvent);
        }
        else {
            this.updateSingleSelection(option, shouldEmitEvent);
        }
        this.updateOptions();
        // Restore the scroll position
        if (this.listBoxElement && this.multiple) {
            this.listBoxElement.scrollTop = scrollTop;
        }
    }
    updateSingleSelection(option, shouldEmitEvent) {
        if (this.selectedOptions.includes(option)) {
            return;
        }
        this.selectedOptions = [option];
        this.value = option.ref.value;
        if (shouldEmitEvent) {
            this.handleSelectChange(this.value);
        }
    }
    updateMultipleSelection(option, shouldEmitEvent) {
        if (this.selectedOptions.includes(option)) {
            this.selectedOptions = this.selectedOptions.filter((selectedOption) => selectedOption !== option);
        }
        else {
            this.selectedOptions = [...this.selectedOptions, option];
        }
        this.value = this.selectedOptions.map((option) => option.ref.value);
        if (shouldEmitEvent) {
            this.handleSelectChange(this.value);
        }
    }
    activateOption(optionElement) {
        if (optionElement) {
            this.activeOptionIndex = this.options.findIndex((option) => (option === null || option === void 0 ? void 0 : option.ref) === optionElement);
            this.activeOptionElement = optionElement;
            this.updateOptions();
        }
    }
    activateMatchingOption(keystrokeFilterTerm) {
        const matchingOption = this.getOptionByKeystrokeFilterTerm(keystrokeFilterTerm, this.activeOptionIndex || 0);
        if (matchingOption) {
            this.activateOption(matchingOption === null || matchingOption === void 0 ? void 0 : matchingOption.ref);
        }
        else {
            this.clearKeystrokeFilterTerm();
        }
    }
    getOptionByKeystrokeFilterTerm(KeystrokeFilterTerm, index) {
        const filteredOptions = this.options.filter((option) => option.label.toLowerCase().startsWith(KeystrokeFilterTerm.toLowerCase()));
        const directMatch = filteredOptions[0];
        const allSameCharacter = KeystrokeFilterTerm.split('').every((char) => char === KeystrokeFilterTerm[0]);
        if (directMatch) {
            return directMatch;
        }
        /**
         * When all characters of the search term are the same (e.g. `bbb`)
         * we cycle through options that start with `b`
         */
        if (allSameCharacter) {
            // cycling through options by reordering them based on the current active option
            const orderedOptions = [...this.options.slice(index + 1), ...this.options.slice(0, index + 1)];
            const filteredOptions = orderedOptions.filter((option) => option.label.toLowerCase().startsWith(KeystrokeFilterTerm[0].toLowerCase()));
            return filteredOptions[0];
        }
    }
    getKeystrokeFilterTerm(key) {
        clearTimeout(this.keystrokeFilterTimer);
        this.keystrokeFilterTimer = setTimeout(() => this.clearKeystrokeFilterTerm(), 500);
        this.keystrokeFilterTerm += key;
        return this.keystrokeFilterTerm;
    }
    clearKeystrokeFilterTerm() {
        this.keystrokeFilterTerm = '';
    }
    async clearSelect() {
        await forceFocus(this.comboBoxElement);
        this.resetActiveOption();
        this.selectedOptions = [];
        if (this.multiple) {
            this.value = [];
        }
        else {
            this.value = '';
        }
        this.inputchangeEvent.emit({ value: this.value });
        this.updateOptions();
    }
    isActiveOptionIndexSet() {
        return typeof this.activeOptionIndex !== 'undefined' && this.activeOptionIndex >= 0;
    }
    getActionFromCode(code, key, ctrlKey, altKey, metaKey) {
        // when open or closed
        if (code === 'Home') {
            return this.selectActions.FIRST;
        }
        if (code === 'End') {
            return this.selectActions.LAST;
        }
        if (!this.searchable &&
            (code === 'Backspace' || code === 'Clear' || (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey))) {
            // We only want the rover search to be applied when the select is not searchable. Otherwise all keyboard input should be passed to the search input
            return this.selectActions.ROVER_TYPE;
        }
        // when closed
        if (!this.isListBoxVisible) {
            if (['ArrowDown', 'Enter', 'Space'].includes(code)) {
                if (!this.searchable) {
                    return this.selectActions.OPEN_AND_ACTIVATE_OPTION;
                }
                else {
                    return this.selectActions.OPEN_AND_FOCUS_SEARCH;
                }
            }
            if (code === 'ArrowUp') {
                return this.selectActions.FIRST;
            }
        }
        // when open
        if (this.isListBoxVisible) {
            if (code === 'Tab') {
                return this.selectActions.SELECT_OPTION_AND_CLOSE;
            }
            if (code === 'Escape') {
                return this.selectActions.CLOSE;
            }
            if (code === 'PageDown') {
                return this.selectActions.INCREMENT_ACTIVE_OPTION;
            }
            if (code === 'PageUp') {
                return this.selectActions.DECREMENT_ACTIVE_OPTION;
            }
            if (code === 'ArrowUp' && altKey) {
                return this.selectActions.SELECT_OPTION_AND_CLOSE;
            }
            if (code === 'ArrowUp') {
                return this.selectActions.ACTIVATE_PREV_OPTION;
            }
            if (code === 'ArrowDown' && !altKey) {
                return this.selectActions.ACTIVATE_NEXT_OPTION;
            }
            if (code === 'Enter' || code === 'Space') {
                if (this.isActiveOptionIndexSet()) {
                    if (this.multiple) {
                        return this.selectActions.SELECT_OPTION;
                    }
                    return this.selectActions.SELECT_OPTION_AND_CLOSE;
                }
            }
        }
    }
    initTippy() {
        if (isNodeEnvironment()) {
            return;
        }
        if (this.inputElement && this.listBoxContainerElement) {
            const listBoxContainerElement = this.listBoxContainerElement;
            this.tippyInstance = tippy(this.inputElement, {
                appendTo: 'parent',
                trigger: 'manual',
                placement: 'bottom',
                hideOnClick: false,
                showOnCreate: true,
                offset: [0, 8],
                aria: {
                    content: null,
                    expanded: false,
                },
                interactive: true,
                popperOptions: {
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top', 'bottom'],
                                boundary: 'viewport',
                            },
                        },
                    ],
                },
                render() {
                    return {
                        popper: listBoxContainerElement,
                    };
                },
            });
            this.tippyInstance.hide();
        }
    }
    initOptions() {
        // In SSR mode, the element is not available yet
        if (!this.element)
            return;
        const options = Array.from(this.element.querySelectorAll(`${transformTagName('cosmos-option')}`));
        this.optgroups = Array.from(this.element.querySelectorAll(`${transformTagName('cosmos-optgroup')}`));
        // check if there any options yet before initializing
        if (options && options.length === 0) {
            return;
        }
        this.options = options.map((optionElement) => {
            var _a;
            return {
                index: options.indexOf(optionElement),
                label: ((_a = optionElement.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || '',
                ref: optionElement,
            };
        });
        this.filteredOptions = this.options;
        if (this.searchable) {
            this.fuseSearchInstance = new Fuse(this.options, this.fuseSearchOptions);
        }
        this.multiple ? this.initMultipleSelection() : this.initSingleSelection();
        this.updateOptions();
    }
    initMultipleSelection() {
        const preSelectedOptions = this.getPreSelectedOptions();
        this.selectedOptions = preSelectedOptions;
        this.value = preSelectedOptions.map((option) => option.ref.value);
    }
    initSingleSelection() {
        const preSelectedOption = this.getPreSelectedOption();
        if (preSelectedOption) {
            this.selectOption(preSelectedOption, false);
        }
    }
    updateOptions(shouldFocus = true) {
        this.isUpdatingOptions = true; // Prevent updating options while iterating over them due to `optionselect` event
        let positionInSet = 0;
        const totalVisibleOptions = this.searchable ? this.filteredOptions.length : this.options.length;
        this.options.forEach((option, index) => {
            // Set tabIndex to -1 for all options to manage focus dynamically when using keyboard navigation.
            // When not using keyboard, set tabIndex to 0 to ensure all options are still accessible to AT.
            option.ref.tabIndex = this.isNavigatedWithKeyboard ? -1 : 0;
            if (this.selectedOptions.includes(option)) {
                option.ref.setAttribute('selected', '');
            }
            else {
                option.ref.removeAttribute('selected');
            }
            if (index === this.activeOptionIndex && shouldFocus) {
                option.ref.tabIndex = 0;
                option.ref.focus();
            }
            if (this.searchable) {
                if (!this.filteredOptions.includes(option)) {
                    option.ref.style.display = 'none';
                    option.ref.tabIndex = -1;
                    option.ref.removeAttribute('aria-posinset');
                    option.ref.removeAttribute('aria-setsize');
                    return;
                }
                option.ref.style.display = '';
            }
            positionInSet++;
            // Update position and total visible options count for visible/accessible options
            option.ref.setAttribute('aria-posinset', String(positionInSet));
            option.ref.setAttribute('aria-setsize', String(totalVisibleOptions));
        });
        this.isUpdatingOptions = false;
    }
    calculateMultipleOverflowCount() {
        if (!this.multiple || this.selectedOptions.length === 0) {
            this.multipleOverflowCount = 0;
            return;
        }
        const valueSlot = this.element.querySelector('[slot="value"]');
        const selectValues = Array.from(valueSlot.querySelectorAll('[multiple]'));
        const valueSlotRect = valueSlot.getBoundingClientRect();
        const overflowIndex = selectValues.findIndex((value) => {
            const selectValueRect = value.getBoundingClientRect();
            const ellipsisBuffer = 40;
            return selectValueRect ? selectValueRect.left + ellipsisBuffer > valueSlotRect.right : false;
        });
        this.multipleOverflowCount = overflowIndex !== -1 ? selectValues.length - overflowIndex : 0;
    }
    filterOptions(filterTerm) {
        if (!filterTerm) {
            this.filteredOptions = this.options;
        }
        else {
            const fuzzySearchResults = this.fuseSearchInstance.search(filterTerm);
            const filteredLabels = new Set(fuzzySearchResults.map((result) => result.item.label));
            this.filteredOptions = this.options.filter((option) => filteredLabels.has(option.label));
        }
        this.updateOptions(false);
    }
    observeOptionChanges() {
        this.mutationObserver = new MutationObserver((mutationList) => {
            const mutatedElements = mutationList.reduce((acc, mutation) => {
                acc.push(...Array.from(mutation.addedNodes).map((node) => { var _a; return (_a = node === null || node === void 0 ? void 0 : node.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }));
                acc.push(...Array.from(mutation.removedNodes).map((node) => { var _a; return (_a = node === null || node === void 0 ? void 0 : node.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }));
                return acc;
            }, []);
            if (mutatedElements.includes(transformTagName('cosmos-option')) ||
                mutatedElements.includes(transformTagName('cosmos-optgroup'))) {
                this.initOptions();
            }
        });
        this.mutationObserver.observe(this.element, { childList: true });
    }
    connectedCallback() {
        this.form = this.element.closest('form');
        if (this.form) {
            this.form.addEventListener('formdata', this.handleFormData, true);
            this.form.addEventListener('submit', this.handleFormSubmit, true);
        }
        if (!isNodeEnvironment()) {
            this.observeOptionChanges();
            this.resizeObserver = new ResizeObserver(() => this.calculateMultipleOverflowCount());
            this.resizeObserver.observe(this.element);
        }
    }
    componentWillLoad() {
        this.inputInvalid = this.invalid;
    }
    componentDidLoad() {
        this.initOptions();
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'light', light: 'dark' },
            callback: this.handleAppearanceChange.bind(this),
        });
        this.initTippy();
        if (this.autofocus) {
            this.initAutofocus();
        }
    }
    componentDidRender() {
        this.calculateMultipleOverflowCount();
    }
    disconnectedCallback() {
        var _a;
        if (this.form) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
        }
        global$1.window.removeEventListener('click', this.handleDocumentClick);
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.element);
        }
        if (this.mutationObserver) {
            (_a = this.mutationObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
        }
    }
    render() {
        return (h("div", { key: '894d62cd52f7779a57696cd5300d4d92a6271623', class: generateClassNames({ multiple: this.selectedOptions.length > 1 }), part: "base" }, h(FormField, { key: '44cb31a93b683ff8a77163f19678c930a242420a', element: this.element, appearance: this.computedAppearance, label: this.label, helpText: this.helpText, optionalLabel: this.optionalLabel, requiredLabel: this.requiredLabel, disabled: this.disabled, invalid: this.inputInvalid, validationMessage: this.getValidationMessage(), validationMessageRef: (el) => (this.validationMessageRef = el), labelForValue: "combobox", required: this.required }, h("div", { key: '51b725bb900ae2b133c3f29416acfeaa0a9383f2', class: generateClassNames('input', {
                appearance: this.computedAppearance,
                placeholder: !this.selectedOptions.length,
                disabled: this.disabled,
                invalid: this.inputInvalid,
            }), onClick: this.handleSelectClick, ref: (el) => {
                this.inputElement = el;
            }, part: "select" }, h("div", { key: '7d6ed8493327236de4ea7510753f1526c90db217', "aria-autocomplete": this.searchable ? 'list' : 'none', "aria-controls": "listbox", "aria-describedby": getAriaDescribedBy(this.inputInvalid), "aria-disabled": getAriaDisabled(this.disabled), "aria-expanded": getAriaExpanded(this.isListBoxVisible), "aria-haspopup": "listbox", "aria-invalid": getAriaInvalid(this.inputInvalid), "aria-labelledby": "label", "aria-required": getAriaRequired(this.required), class: generateClassNames('combobox'), id: "combobox", ref: (el) => {
                this.comboBoxElement = el;
            }, role: "combobox", tabIndex: this.disabled ? -1 : 0 }, this.selectedOptions.length > 0 && (h("span", { key: 'd153a6b215a0a6952dccae1c1a124e3fe6030d9c', class: generateClassNames('sr-value') }, this.selectedOptions.map((option) => option.label).join(', '))), h("slot", { key: 'b72c422dd4a7ec23ec58e10ad6c58ef988f774a1', onSlotchange: this.handleValueSlotChange, name: "value", "aria-hidden": "true" }, h("cosmos-text", { key: 'a8dd422bafcc625e941f2722081f6994ba30cf8d', appearance: this.computedAppearance, clamp: 1, class: generateClassNames('placeholder'), kind: "subtle", size: "small" }, this.placeholder))), h("div", { key: '202ae4e87603755631d49b476e7d08a661995883', class: generateClassNames('focus-ring') }), this.multipleOverflowCount > 0 && (h("cosmos-badge", { key: 'f1f21edf06eeee3bda4abd7ccdaf7ddae3ca4684', appearance: this.computedAppearance, class: generateClassNames('overflow-badge'), size: "small" }, "+", this.multipleOverflowCount)), !!this.clearable && !!this.selectedOptions.length && (h("cosmos-button", { key: 'ecfc1077c89fb8a98a35436826065da9fdda51fc', "aria-label": this.clearAriaLabel, "aria-hidden": !this.clearAriaLabel, appearance: this.computedAppearance, class: generateClassNames('clearable-button'), kind: "tertiary", onClick: this.handleClearableClick, onKeyDown: this.handleClearableKeyDown, onMouseDown: (e) => e.preventDefault(), shape: "circle", size: "small" }, h("cosmos-icon-close", { key: 'bced60694205ac90d06a56c5b024b253e9e33c75', slot: "icon" }))), h("cosmos-icon-chevron-down", { key: 'ef34121480d343194c3725f2eed491526133eb55', class: generateClassNames('icon') })), h("div", { key: '43ddbae32018fbe4ef5980797175da70bd3fb353', class: generateClassNames('listbox-container', {
                appearance: this.computedAppearance,
                open: this.isListBoxVisible,
                filtered: this.searchable && this.searchTerm !== '',
            }), onClick: this.handleListBoxClick, ref: (el) => {
                this.listBoxContainerElement = el;
            } }, this.searchable && (h("div", { key: '2c360de350e1a7b2dccbb090f97e7599bd15b627', class: generateClassNames('search-container') }, h("cosmos-input", { key: '4484335e66e9c98b8800659468c16cdaa5c67b0d', class: generateClassNames('search'), clearable: true, onInputinput: this.handleSearchInput, placeholder: this.searchPlaceholder, ref: (el) => {
                this.searchInputElement = el;
            }, value: this.searchTerm, autocomplete: "off" }, h("cosmos-icon-search", { key: '2bfcbdaa4635312af3a1af2d1a6d22078a90b4bc', slot: "icon" })))), h("div", { key: '68d0fd842df37a21e9a7e2b1c9d4c57780cf76da', "aria-multiselectable": this.multiple ? 'true' : null, class: generateClassNames('listbox'), id: "listbox", part: "options", role: "listbox", tabIndex: -1, ref: (el) => {
                this.listBoxElement = el;
            } }, h("slot", { key: '58a374d16301ed4d9931140dc200fa315e7dafea', onSlotchange: this.handleSlotChange }), this.searchable && this.filteredOptions.length === 0 && (h("cosmos-text", { key: '2df89abf9e791849b96abf63982593bcfe99428f', class: generateClassNames('no-filter-results'), kind: "subtle", size: "small" }, this.searchEmptyResultsText)))))));
    }
    get element() { return this; }
    static get watchers() { return {
        "invalid": ["handleInvalidChange"],
        "value": ["handleValueChange"],
        "appearance": ["handleAppearanceChange"],
        "selectedOptions": ["handleSelectedOptionsChange"],
        "searchTerm": ["handleSearchTermChange"]
    }; }
    static get style() { return CosmosSelectStyle0; }
}, [1, "cosmos-select", {
        "appearance": [513],
        "autofocus": [516],
        "clearable": [516],
        "clearAriaLabel": [513, "clear-aria-label"],
        "disabled": [516],
        "helpText": [513, "help-text"],
        "invalid": [516],
        "label": [513],
        "multiple": [516],
        "name": [513],
        "optionalLabel": [513, "optional-label"],
        "placeholder": [513],
        "required": [516],
        "requiredLabel": [513, "required-label"],
        "searchable": [516],
        "searchEmptyResultsText": [513, "search-empty-results-text"],
        "searchPlaceholder": [513, "search-placeholder"],
        "validationMessage": [513, "validation-message"],
        "value": [1025],
        "activeOptionElement": [32],
        "activeOptionIndex": [32],
        "computedAppearance": [32],
        "inputInvalid": [32],
        "isListBoxVisible": [32],
        "multipleOverflowCount": [32],
        "searchTerm": [32],
        "selectedOptions": [32],
        "inputBlur": [64],
        "inputFocus": [64],
        "inputCheckValidity": [64],
        "inputReportValidity": [64]
    }, [[0, "optionselect", "handleOptionSelect"], [0, "keydown", "handleKeyDown"]], {
        "invalid": ["handleInvalidChange"],
        "value": ["handleValueChange"],
        "appearance": ["handleAppearanceChange"],
        "selectedOptions": ["handleSelectedOptionsChange"],
        "searchTerm": ["handleSearchTermChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-select", "cosmos-badge", "cosmos-button", "cosmos-icon-chevron-down", "cosmos-icon-close", "cosmos-icon-search", "cosmos-icon-warning", "cosmos-input", "cosmos-select-value", "cosmos-spinner", "cosmos-text"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosSelect$1);
            }
            break;
        case "cosmos-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "cosmos-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "cosmos-icon-chevron-down":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "cosmos-icon-close":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "cosmos-icon-search":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "cosmos-icon-warning":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "cosmos-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "cosmos-select-value":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "cosmos-spinner":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "cosmos-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const CosmosSelect = CosmosSelect$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosSelect, defineCustomElement };
